import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { getDigitsFor } from '../utils/symbolDigitsStore';
import { createChart, ColorType, LineStyle, LineType, CandlestickSeries, LineSeries, HistogramSeries, createSeriesMarkers, AreaSeries, BaselineSeries } from 'lightweight-charts';
import { usePriceFeed } from '../context/PriceFeedContext';
import { useAccountData } from '../context/AccountDataProvider';
import { getIndicatorKey, padIndicatorData } from '../utils/indicatorUtils';
import IndicatorControls from './IndicatorControls';
import { Button } from './ui/button';
import CustomSelect from './ui/CustomSelect';
import { listChartProfiles, createChartProfile, deleteChartProfile } from '../api/api';
import { getAccountTrades } from '../api/api';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { useDateTimeFormatting } from '../utils/datetime';
import { DrawingToolsManager, TOOL_TYPES } from '../utils/drawingToolsManager';
import {
    buildOpenPositionTradeOverlay,
    getOpenPositionOverlaySpecs,
} from './tradingChartPositionOverlays';
import { buildHistoricalTradeOverlay } from './tradingChartHistoricalTradeMarkers';

// Converts OHLCV data (d.x: ms, d.y: [open, high, low, close])
function convertOhlcvToBars(ohlcvData) {
    if (!Array.isArray(ohlcvData)) return [];
    return ohlcvData
        .filter(d =>
            d &&
            typeof d.time === "number" &&
            typeof d.open === "number" &&
            typeof d.high === "number" &&
            typeof d.low === "number" &&
            typeof d.close === "number"
        )
        .map(d => {
            let timeInSeconds = d.time;
            // If time is in milliseconds, convert to seconds
            if (timeInSeconds > 1000000000000) {
                timeInSeconds = Math.floor(timeInSeconds / 1000);
            }
            return {
                time: timeInSeconds,
                open: d.open,
                high: d.high,
                low: d.low,
                close: d.close,
            };
        });
}

const normalizeChartTime = (time) => {
    if (typeof time !== 'number' || !Number.isFinite(time)) return null;
    return time > 1000000000000 ? Math.floor(time / 1000) : time;
};

const sortedBarTimes = (bars = []) => {
    if (!Array.isArray(bars)) return [];
    return bars
        .map((bar) => normalizeChartTime(bar?.time))
        .filter((value) => value !== null)
        .sort((a, b) => a - b);
};

const medianBarInterval = (barTimes = []) => {
    const intervals = [];
    for (let i = 1; i < barTimes.length; i += 1) {
        const diff = barTimes[i] - barTimes[i - 1];
        if (diff > 0) intervals.push(diff);
    }
    return intervals.length
        ? intervals.sort((a, b) => a - b)[Math.floor(intervals.length / 2)]
        : 86400;
};

const snapTimeToContainingBar = (time, bars = []) => {
    const normalized = normalizeChartTime(time);
    if (normalized === null || !Array.isArray(bars) || bars.length === 0) return normalized;

    const barTimes = sortedBarTimes(bars);
    if (!barTimes.length) return normalized;
    if (barTimes.includes(normalized)) return normalized;

    const baseInterval = medianBarInterval(barTimes);
    if (normalized < barTimes[0]) {
        return barTimes[0] - normalized <= baseInterval * 1.5 ? barTimes[0] : normalized;
    }

    for (let i = 1; i < barTimes.length; i += 1) {
        if (barTimes[i] > normalized) return barTimes[i - 1];
    }

    const lastTime = barTimes[barTimes.length - 1];
    return normalized - lastTime <= baseInterval * 1.5 ? lastTime : normalized;
};

const getBarAnchorTime = (bar) => {
    const time = normalizeChartTime(bar?.time_real ?? bar?.time);
    return time;
};

const findBarIndexByAnchorTime = (bars = [], anchorTime) => {
    const normalizedAnchor = normalizeChartTime(anchorTime);
    if (normalizedAnchor === null || !Array.isArray(bars)) return -1;
    return bars.findIndex((bar) => getBarAnchorTime(bar) === normalizedAnchor);
};

const buildViewportRestoreCandidate = (range, bars = []) => {
    if (!range || !Array.isArray(bars) || bars.length === 0) return null;
    const from = range.from;
    const to = range.to;
    if (typeof from !== 'number' || typeof to !== 'number' || !Number.isFinite(to - from) || to <= from) {
        return null;
    }
    const anchorIndex = Math.min(
        bars.length - 1,
        Math.max(0, Math.floor(from))
    );
    const anchorTime = getBarAnchorTime(bars[anchorIndex]);
    if (anchorTime === null) return null;
    return {
        from,
        to,
        oldLen: bars.length,
        anchorIndex,
        anchorOffset: from - anchorIndex,
        anchorTime,
    };
};

// Converts trades to chart markers
function convertMarkers(tradeMarkers, bars = []) {
    if (!Array.isArray(tradeMarkers)) return [];
    const flatMarkers = tradeMarkers.flatMap(t => {
        const entryTimeInSeconds = snapTimeToContainingBar(t.entry_timestamp, bars);
        if (entryTimeInSeconds === null) return [];
        const entry = {
            time: entryTimeInSeconds,
            position: "belowBar",
            color: t.direction === "BUY" ? "#26a69a" : "#ef5350",
            shape: "arrowUp",
            text: `${t.direction}`,
        };
        let exit = null;
        if (t.exit_timestamp && t.exit_price !== null) {
            const exitTimeInSeconds = snapTimeToContainingBar(t.exit_timestamp, bars);
            if (exitTimeInSeconds !== null) {
                exit = {
                    time: exitTimeInSeconds,
                    position: "aboveBar",
                    color: t.pnl >= 0 ? "#26a69a" : "#ef5350",
                    shape: "arrowDown",
                    text: `Exit: ${t.pnl.toFixed(0)}`,
                };
            }
        }
        return exit ? [entry, exit] : [entry];
    });

    return flatMarkers
        .filter(m => typeof m.time === "number" && !isNaN(m.time))
        .sort((a, b) => a.time - b.time);
}

// De-duplicate data by time, keeping the latest entry and returning strict ascending order
function deduplicateData(data) {
    if (!Array.isArray(data) || data.length === 0) return [];
    const byTime = new Map();
    data.forEach((item) => {
        const time = normalizeChartTime(item?.time);
        if (time === null) return;
        byTime.set(time, { ...item, time });
    });
    return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
}

const indicatorColors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#FFA500', // Orange
    '#800080', // Purple
    '#008000', // Dark Green
    '#800000', // Maroon
];

// Default color for negative values when using baseline coloring on pane indicators
const NEGATIVE_LINE_COLOR = '#ff4d4f';
const CHART_INITIAL_RESIZE_DELAYS_MS = [0, 16, 50, 120, 250, 500];

// ---- Visual schema helpers ----
const lineTypeMap = {
    simple: LineType?.Simple ?? 0,
    withsteps: LineType?.WithSteps ?? 1,
    curved: LineType?.Curved ?? 2,
};

const lineStyleMap = {
    solid: LineStyle.Solid ?? 0,
    dotted: LineStyle.Dotted ?? 1,
    dashed: LineStyle.Dashed ?? 2,
};

function deepMerge(target, source) {
    if (!source) return target ? JSON.parse(JSON.stringify(target)) : {};
    if (!target) return JSON.parse(JSON.stringify(source));
    const output = Array.isArray(target) ? [...target] : { ...target };
    Object.keys(source).forEach((key) => {
        const sVal = source[key];
        const tVal = output[key];
        if (sVal && typeof sVal === 'object' && !Array.isArray(sVal)) {
            output[key] = deepMerge(tVal && typeof tVal === 'object' ? tVal : {}, sVal);
        } else {
            output[key] = sVal;
        }
    });
    return output;
}

function resolveSeriesType(plotType) {
    switch ((plotType || 'line').toLowerCase()) {
        case 'area':
            return { SeriesCtor: AreaSeries, type: 'area' };
        case 'histogram':
            return { SeriesCtor: HistogramSeries, type: 'histogram' };
        case 'baseline':
            return { SeriesCtor: BaselineSeries, type: 'baseline' };
        case 'line':
        default:
            return { SeriesCtor: LineSeries, type: 'line' };
    }
}

function buildOptionsFromVisuals(visualForKey, fallbacks = {}) {
    const opts = {};
    if (visualForKey) {
        if (typeof visualForKey.color === 'string') opts.color = visualForKey.color;
        if (typeof visualForKey.lineWidth === 'number') opts.lineWidth = visualForKey.lineWidth;
        if (typeof visualForKey.lineStyle === 'string') {
            opts.lineStyle = lineStyleMap[visualForKey.lineStyle] ?? undefined;
        }
        if (typeof visualForKey.lineType === 'string') {
            opts.lineType = lineTypeMap[visualForKey.lineType.toLowerCase()] ?? undefined;
        }
        if (typeof visualForKey.visible === 'boolean') opts.visible = visualForKey.visible;
    }
    // apply fallbacks when not provided by visuals
    if (opts.color === undefined && fallbacks.color) opts.color = fallbacks.color;
    if (opts.lineWidth === undefined && fallbacks.lineWidth) opts.lineWidth = fallbacks.lineWidth;
    if (opts.lineStyle === undefined && fallbacks.lineStyle !== undefined) opts.lineStyle = fallbacks.lineStyle;
    return opts;
}

// Convert generic options to series-type-specific options
function toSeriesTypeOptions(seriesType, opts) {
    const type = seriesType; // 'line' | 'area' | 'histogram'
    if (type === 'line') {
        const lineOpts = {
            color: opts.color,
            lineWidth: opts.lineWidth,
            lineStyle: opts.lineStyle,
            visible: opts.visible,
        };
        if (opts.lineType !== undefined) lineOpts.lineType = opts.lineType;
        return lineOpts;
    }
    if (type === 'baseline') {
        // Show only colored line above/below base by making fills transparent
        const neg = opts.negativeColor || NEGATIVE_LINE_COLOR;
        return {
            baseValue: 0,
            topLineColor: opts.color || '#FFD700',
            bottomLineColor: neg,
            topFillColor1: 'rgba(0,0,0,0)',
            topFillColor2: 'rgba(0,0,0,0)',
            bottomFillColor1: 'rgba(0,0,0,0)',
            bottomFillColor2: 'rgba(0,0,0,0)',
            lineWidth: opts.lineWidth,
            visible: opts.visible,
        };
    }
    if (type === 'histogram') {
        return {
            color: opts.color,
            // histogram respects color; width/style not applicable
            visible: opts.visible,
        };
    }
    // area
    const color = opts.color || '#FFD700';
    // Derive semi-transparent fills from color; simple fallback if not hex
    const hex = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(color);
    const withAlpha = (hexColor, alpha) => {
        if (!/^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(hexColor)) return color;
        const c = hexColor.replace('#','');
        const expand = c.length === 3 ? c.split('').map(ch => ch+ch).join('') : c;
        const r = parseInt(expand.slice(0,2),16);
        const g = parseInt(expand.slice(2,4),16);
        const b = parseInt(expand.slice(4,6),16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    return {
        lineColor: color,
        topColor: hex ? withAlpha(color, 0.2) : color,
        bottomColor: hex ? withAlpha(color, 0.02) : color,
        lineWidth: opts.lineWidth,
        visible: opts.visible,
    };
}

// Determine a sensible precision for a series based on its smallest non-zero value.
// This ensures very small indicator values (e.g. ATR on forex) are not shown as 0.00.
function computePrecisionForPoints(points, defaultPrecision = 2, maxPrecision = 8) {
    if (!Array.isArray(points) || points.length === 0) return defaultPrecision;
    let minNonZero = Infinity;
    for (const p of points) {
        const v = p && (p.value !== undefined ? p.value : (p.y !== undefined ? p.y : undefined));
        if (typeof v !== 'number' || !Number.isFinite(v)) continue;
        const a = Math.abs(v);
        if (a > 0 && a < minNonZero) minNonZero = a;
    }
    if (!Number.isFinite(minNonZero) || minNonZero === Infinity) return defaultPrecision;
    if (minNonZero >= 1) return Math.max(defaultPrecision, 2);
    const precision = Math.ceil(-Math.log10(minNonZero));
    return Math.min(Math.max(defaultPrecision, precision), maxPrecision);
}

function derivePriceFormatFromPoints(points, cachedDigits) {
    const defaultPrecision = Number.isFinite(cachedDigits) ? Number(cachedDigits) : 2;
    const precision = computePrecisionForPoints(points, defaultPrecision, 8);
    const pf = { type: 'price', precision };
    // Derive a sensible minMove when digits are known
    if (Number.isFinite(precision) && precision >= 0 && precision <= 12) {
        pf.minMove = Math.pow(10, -precision);
    }
    return pf;
}

// Resolve indicator metadata whether availableIndicators is an array or an object map
function getIndicatorMeta(availableIndicators, name) {
    if (!availableIndicators || !name) return null;
    if (Array.isArray(availableIndicators)) {
        return availableIndicators.find((m) => m?.name === name) || null;
    }
    // Object map cases
    if (typeof availableIndicators === 'object') {
        if (availableIndicators[name]) return availableIndicators[name];
        if (availableIndicators.indicators && availableIndicators.indicators[name]) {
            return availableIndicators.indicators[name];
        }
        const vals = Object.values(availableIndicators);
        const found = vals.find((m) => m && (m.name === name || m.display_name === name));
        return found || null;
    }
    return null;
}

// Resolve which series key to use from visualsByKey given candidates
function pickSeriesVisual(visualsByKey, indicatorMeta, candidateKey) {
    if (!visualsByKey || typeof visualsByKey !== 'object') return { key: null, visual: undefined };
    // 1) direct match
    if (candidateKey && visualsByKey[candidateKey]) return { key: candidateKey, visual: visualsByKey[candidateKey] };
    // 1b) suffix match for prefixed keys e.g., dmi_plus_di -> plus_di
    if (candidateKey) {
        const c = String(candidateKey).toLowerCase();
        for (const k of Object.keys(visualsByKey)) {
            if (c.endsWith(String(k).toLowerCase())) {
                return { key: k, visual: visualsByKey[k] };
            }
        }
    }
    // 2) default
    if (visualsByKey.default) return { key: 'default', visual: visualsByKey.default };
    // 3) match any known output name
    if (indicatorMeta?.outputs && Array.isArray(indicatorMeta.outputs)) {
        for (const out of indicatorMeta.outputs) {
            if (visualsByKey[out]) return { key: out, visual: visualsByKey[out] };
        }
    }
    // 4) single key
    const keys = Object.keys(visualsByKey);
    if (keys.length === 1) return { key: keys[0], visual: visualsByKey[keys[0]] };
    return { key: null, visual: undefined };
}

// Heuristic: detect very small-scale outputs (e.g., EMA slope) that should live in their own pane
function isSlopeOutputKey(key) {
    const k = String(key || '').toLowerCase();
    return k.endsWith('_slope') || k.endsWith('_slope_pct') || /(^|_)slope(_|$)/.test(k);
}

// Distinguish percentage-form slope keys
function isSlopePercentKey(key) {
    const k = String(key || '').toLowerCase();
    return k.endsWith('_slope_pct') || k.endsWith('_slope_pc') || /(^|_)slope_pct($|_)/.test(k) || /(^|_)slope_pc($|_)/.test(k);
}

// Determine which slope output user prefers based on params or visuals
function chooseAllowedSlopeKeys(allKeys, params = {}, visualsByKey = {}) {
    const slopeKeys = (allKeys || []).filter(isSlopeOutputKey);
    if (slopeKeys.length <= 1) return new Set(slopeKeys);
    // 1) Check visuals: if exactly one slope key is visible, pick it
    try {
        const visibleSlope = slopeKeys.filter(k => (visualsByKey?.[k]?.visible !== false));
        const hiddenSlope = slopeKeys.filter(k => (visualsByKey?.[k]?.visible === false));
        if (visibleSlope.length === 1) return new Set([visibleSlope[0]]);
        if (visibleSlope.length === 0 && hiddenSlope.length === slopeKeys.length - 1) {
            // All but one are hidden; pick the remaining visible (none) → default fallback below
        }
    } catch { /* ignore */ }
    // 2) Check params: slope_output / slope_type / slope_mode string
    const prefStr = String(params?.slope_output || params?.slope_type || params?.slope_mode || '').toLowerCase();
    if (prefStr) {
        if (/(percent|percentage|pct|pc)/.test(prefStr)) {
            const key = slopeKeys.find(isSlopePercentKey) || slopeKeys[0];
            return new Set([key]);
        }
        if (/(delta|value|raw)/.test(prefStr)) {
            const key = slopeKeys.find(k => isSlopeOutputKey(k) && !isSlopePercentKey(k)) || slopeKeys[0];
            return new Set([key]);
        }
    }
    // 3) Check boolean flags: slope_pct / slope_pc / percent vs slope / delta
    const pctLike = !!(params?.slope_pct || params?.slope_pc || params?.percent || params?.percentage);
    const deltaLike = !!(params?.slope || params?.delta || params?.value || params?.raw);
    if (pctLike && !deltaLike) {
        const key = slopeKeys.find(isSlopePercentKey) || slopeKeys[0];
        return new Set([key]);
    }
    if (deltaLike && !pctLike) {
        const key = slopeKeys.find(k => isSlopeOutputKey(k) && !isSlopePercentKey(k)) || slopeKeys[0];
        return new Set([key]);
    }
    // 4) Default: prefer percent if present, else delta
    const prefer = slopeKeys.find(isSlopePercentKey) || slopeKeys[0];
    return new Set([prefer]);
}

// Persisted cache key for per-indicator visual overrides
const OVERRIDES_STORAGE_KEY = 'indicatorVisualOverrides:v1';

// Stable stringify for params to build a signature
function stableStringify(obj) {
    const seen = new WeakSet();
    const helper = (value) => {
        if (value === null || typeof value !== 'object') return JSON.stringify(value);
        if (Array.isArray(value)) return '[' + value.map(helper).join(',') + ']';
        if (seen.has(value)) return '"[Circular]"';
        seen.add(value);
        const keys = Object.keys(value).sort();
        return '{' + keys.map(k => JSON.stringify(k) + ':' + helper(value[k])).join(',') + '}';
    };
    return helper(obj || {});
}

function getIndicatorSignature(indicator) {
    if (!indicator) return '';
    const name = indicator.name || '';
    const pane = indicator.paneType || '';
    const paramsStr = stableStringify(indicator.params || {});
    return `${name}|${pane}|${paramsStr}`;
}

const TradingChart = (props) => {
     const {
         selectedAccount,
         selectedSymbol,
         selectedTimeframe,
         stopLossPrice,
         takeProfitPrice,
         tickSize,
         // Optional: pip size for better precision on some brokers (e.g., cTrader)
         pipSize,
         // Optional: explicit digits from backend when available
         digits,
         // New props for backtesting
         ohlcvData,
         tradeMarkers,
         extraMarkers = [],
         backtestIndicatorData,
         isBacktest = false,
         height, // If provided, use fixed height; otherwise fill parent
         selectedTrade,
        // New optional props for timeframe overlay
         showTimeframeOverlay = false,
         timeframeOptions,
         onTimeframeChange,
         // New: compact controls
         controlsCompact = false,
         controlsOverlay = false,
         chartTitle,
         // New: chart integration - notify parent when a bar is clicked
         onBarSelected,
         // New: show account trades/open positions overlay (live only)
         showRecentTrades = false,
         onToggleRecentTrades,
         openPositions: openPositionsProp,
         // New: force-show the controls bar even in backtest mode
         showControls = false,
         // Optional: load-more callback for backtest/DW charts
         loadMoreHistoryProp,
     } = props;

    const chartShellRef = useRef(null);
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    // Dedicated volume pane index (assigned lazily when first needed)
    const volumePaneIndexRef = useRef(null);
    // Backtest volume overlay (MA on volume) series refs
    const volumeOverlaySeriesRef = useRef(new Map()); // key -> series
    const [backtestVolumeOverlayLabels, setBacktestVolumeOverlayLabels] = useState([]); // [{id,text,color}]
    const markersRef = useRef(null);
    const historicalTradeMarkersRef = useRef(null);
    const historicalTradeConnectorSeriesRef = useRef([]);
    const indicatorSeriesRef = useRef(new Map());
    const specSeriesRef = useRef(new Map());
    const stopLossLineRef = useRef(null);
    const takeProfitLineRef = useRef(null);
    const openPositionLinesRef = useRef([]);
    const supportResistanceLinesRef = useRef(new Map()); // Map<indicatorId, Array<priceLine>>
    const backtestIndicatorLineRefs = useRef(new Map()); // For backtest indicators
    const backtestSeriesKindRef = useRef(new Map()); // seriesId -> 'line' | 'histogram'
    const visualOverridesCacheRef = useRef(new Map()); // signature -> overrides
    // New: keep a reference of current bars and fast lookup time -> index
    const barsRef = useRef([]);
    const timeToIndexRef = useRef(new Map());
    // Scroll-restore: when backfill prepends bars, store the target range here
    // so the historicalData effect can restore viewport AFTER setData.
    const pendingScrollRestoreRef = useRef(null);
    const isRestoringScrollRef = useRef(false);
    const lastDataContextKeyRef = useRef('');
    // New: keep latest callback without re-initting chart
    const onBarSelectedRef = useRef(onBarSelected);
    useEffect(() => { onBarSelectedRef.current = onBarSelected; }, [onBarSelected]);
    const [historicalTradesForChart, setHistoricalTradesForChart] = useState([]);
    const [isChartFullscreen, setIsChartFullscreen] = useState(false);
    const [fullscreenSupported, setFullscreenSupported] = useState(() => (
        typeof document !== 'undefined' && Boolean(document.fullscreenEnabled)
    ));

    const dispatchChartResize = useCallback(() => {
        if (typeof window === 'undefined') return;

        const emitResize = () => window.dispatchEvent(new Event('resize'));
        emitResize();
        window.requestAnimationFrame?.(emitResize);
        window.setTimeout(emitResize, 160);
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const updateFullscreenState = () => {
            const shell = chartShellRef.current;
            setFullscreenSupported(Boolean(document.fullscreenEnabled && shell?.requestFullscreen));
            setIsChartFullscreen(Boolean(shell && document.fullscreenElement === shell));
            dispatchChartResize();
        };

        updateFullscreenState();
        document.addEventListener('fullscreenchange', updateFullscreenState);
        return () => document.removeEventListener('fullscreenchange', updateFullscreenState);
    }, [dispatchChartResize]);

    const handleToggleChartFullscreen = useCallback(async () => {
        const shell = chartShellRef.current;
        if (typeof document === 'undefined' || !shell || !document.fullscreenEnabled || !shell.requestFullscreen) return;

        try {
            if (document.fullscreenElement === shell) {
                await document.exitFullscreen();
                return;
            }

            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }

            await shell.requestFullscreen();
            dispatchChartResize();
        } catch (error) {
            console.error('Failed to toggle chart fullscreen:', error);
        }
    }, [dispatchChartResize]);

    const clearHistoricalTradeOverlay = useCallback(() => {
        try { historicalTradeMarkersRef.current?.setMarkers([]); } catch { /* ignore */ }
        const chart = chartRef.current;
        historicalTradeConnectorSeriesRef.current.forEach((series) => {
            try { chart?.removeSeries(series); } catch { /* ignore */ }
        });
        historicalTradeConnectorSeriesRef.current = [];
    }, []);

    // ── Drawing tools state ──────────────────────────────────────────────────
    const drawingCanvasRef = useRef(null);
    const drawingManagerRef = useRef(null);
    const [activeDrawingTool, setActiveDrawingTool] = useState(TOOL_TYPES.NONE);
    const [drawingSelection, setDrawingSelection] = useState(false);
    const [drawingCount, setDrawingCount] = useState(0);
    const [drawings, setDrawings] = useState([]);
    const [selectedDrawingId, setSelectedDrawingId] = useState(null);

    // Helper: center the last bar in the visible time range
    const centerOnLastBar = useCallback(() => {
        try {
            const chart = chartRef.current;
            const bars = barsRef.current || [];
            if (!chart || !Array.isArray(bars) || bars.length === 0) return;
            // Don't override when a trade focus is active; that effect will set its own range
            if (props?.selectedTrade) return;
            const ts = chart.timeScale();
            const lastIndex = bars.length - 1;
            const range = ts.getVisibleLogicalRange?.();
            let span = (range && typeof range.from === 'number' && typeof range.to === 'number')
                ? (range.to - range.from)
                : 150; // default span when unknown
            if (!Number.isFinite(span) || span <= 0) span = Math.min(150, Math.max(50, lastIndex + 1));
            let from = lastIndex - span / 2;
            let to = lastIndex + span / 2;
            // Clamp so from doesn't go negative
            if (from < 0) { to -= from; from = 0; }
            ts.setVisibleLogicalRange({ from, to });
    } catch { /* ignore */ }
    }, [props?.selectedTrade]);

    // Volume overlay toggle (local)
    const [showVolume, setShowVolume] = useState(true);
    useEffect(() => {
        if (!showVolume && chartRef.current && volumeSeriesRef.current) {
            chartRef.current.removeSeries(volumeSeriesRef.current);
            volumeSeriesRef.current = null;
        }
    }, [showVolume]);

    // Always call hooks unconditionally; select stub for backtest
    const livePriceFeed = usePriceFeed();
    const backtestStub = {
        candle: null,
        historicalData: [],
        activeIndicators: [],
        availableIndicators: [],
        sendIndicatorAction: () => {},
        setActiveIndicators: () => {},
        indicatorLiveUpdate: null,
        isLoggingEnabled: false,
        // Spec streaming fallbacks (no-ops for backtest mode)
        specStreams: {},
        subscribeSpec: () => {},
        unsubscribeSpec: () => {},
    };
    const priceFeed = isBacktest ? backtestStub : livePriceFeed;

    // One-time auto-center gating state per context (account|symbol|timeframe)
    const centeredContextKeyRef = useRef('');
    const didCenterRef = useRef(false);
    const pendingAutoCenterTimerRef = useRef(null);
    const suppressAutoCenterUntilRef = useRef(0);

    const { candle, historicalData, activeIndicators, availableIndicators, sendIndicatorAction, setActiveIndicators, isLoggingEnabled, indicatorLiveUpdate, loadMoreHistory: loadMoreHistoryFeed, specStreams, subscribeSpec, unsubscribeSpec } = priceFeed;
    // Use the prop callback if provided (DW/backtest), otherwise use the feed's callback (live)
    const loadMoreHistory = loadMoreHistoryProp || loadMoreHistoryFeed;
    const accountData = useAccountData() || {};
    const openPositions = Array.isArray(openPositionsProp) ? openPositionsProp : (accountData.openPositions || []);

    // Map from compressed time → real time so axis/crosshair show correct dates
    const timeRealMapRef = useRef(new Map());
    useEffect(() => {
        const src = isBacktest ? ohlcvData : historicalData;
        if (!Array.isArray(src) || src.length === 0) return;
        const m = new Map();
        for (const bar of src) {
            if (bar.time_real != null && bar.time_real !== bar.time) m.set(bar.time, bar.time_real);
        }
        timeRealMapRef.current = m;
    }, [isBacktest, ohlcvData, historicalData]);

    // Profiles (only for live trading)
    const [chartProfiles, setChartProfiles] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState('');

    const normalizeChartProfiles = useCallback((data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.results)) return data.results;
        return [];
    }, []);

    useEffect(() => {
        if (!isBacktest) {
            listChartProfiles()
                .then((data) => setChartProfiles(normalizeChartProfiles(data)))
                .catch(console.error);
        }
    }, [isBacktest, normalizeChartProfiles]);

    // Init main chart
    // User timezone/locale-aware time formatter
    const prefs = useUserPreferences();
    const { formatDate, formatDateTime } = useDateTimeFormatting();
    const toMs = (t) => {
        if (typeof t === 'number') return t > 1000000000000 ? t : t * 1000; // seconds->ms
        if (t && typeof t === 'object' && typeof t.year === 'number') return new Date(t.year, (t.month || 1) - 1, t.day || 1).getTime();
        return undefined;
    };
    // Axis (tick marks) should be concise; use tickMarkType
    // Resolve compressed time → real time for display
    const axisFmtFn = useMemo(() => (t, tickMarkType) => {
        const real = timeRealMapRef.current.get(t) ?? t;
        const ms = toMs(real);
        if (!ms) return '';
        switch (tickMarkType) {
            // 4: TimeWithSeconds, 3: Time, 2: DayOfMonth, 1: Month, 0: Year (LW charts enum)
            case 4:
                return formatDate?.(ms, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) || '';
            case 3:
                return formatDate?.(ms, { hour: '2-digit', minute: '2-digit' }) || '';
            case 2:
                return formatDate?.(ms, { month: 'short', day: 'numeric' }) || '';
            case 1:
                return formatDate?.(ms, { month: 'short', year: 'numeric' }) || '';
            case 0:
            default:
                return formatDate?.(ms, { year: 'numeric' }) || '';
        }
    }, [formatDate]);
    // Crosshair/tooltip can stay rich
    const crosshairTimeFmt = useMemo(() => (t) => {
        const real = timeRealMapRef.current.get(t) ?? t;
        const ms = toMs(real);
        if (!ms) return '';
        return formatDateTime?.(ms, { second: '2-digit' }) || '';
    }, [formatDateTime]);

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const el = chartContainerRef.current;
        const computeSize = () => {
            const rect = el.getBoundingClientRect?.();
            const width = Math.round(el.clientWidth || rect?.width || 600);
            const measuredHeight = typeof height === 'number'
                ? height
                : Math.round(el.clientHeight || rect?.height || 350);
            return {
                width: Math.max(1, width),
                height: Math.max(1, measuredHeight),
            };
        };
        const { width, height: h } = computeSize();

        const chart = createChart(el, {
            width,
            height: h, // Use computed height
            timeScale: {
                timeVisible: true,
                secondsVisible: true,
                rightOffset: 10,
                tickMarkFormatter: (t, tickType /*, localeStr */) => axisFmtFn(t, tickType),
            },
            localization: { locale: prefs?.locale || 'en-US', timeFormatter: (t) => crosshairTimeFmt(t) },
            // Hide attribution logo
            attributionLogo: false,
            layout: { background: { type: ColorType.Solid, color: '#1E293B' }, textColor: '#CBD5E1', attributionLogo: false },
            grid: { vertLines: { color: '#334155' }, horzLines: { color: '#334155' } },
            crosshair: { mode: 0 },
            priceScale: { borderColor: '#334155' },
        });
        chartRef.current = chart;

    seriesRef.current = chart.addSeries(CandlestickSeries, {
            upColor: '#22C55E',
            downColor: '#EF4444',
            borderVisible: false,
            wickUpColor: '#22C55E',
            wickDownColor: '#EF4444',
        });

    // Do NOT create volume series here; create it lazily when we actually have volume data

        // Subscribe to clicks after chart + series are ready
        let clickHandler = null;
        clickHandler = (param) => {
            let idx = null;
            // 1) Prefer logical index when present
            if (typeof param?.logical === 'number' && Number.isFinite(param.logical)) {
                const maxIndex = Math.max(0, (barsRef.current?.length || 0) - 1);
                const rounded = Math.min(maxIndex, Math.max(0, Math.round(param.logical)));
                idx = rounded;
            }
            // 2) Fallback to time -> index map
            if (idx == null && typeof param?.time === 'number') {
                const fromTime = timeToIndexRef.current.get(param.time);
                if (fromTime != null) idx = fromTime;
            }
            // 3) Fallback to seriesData map
            if (idx == null && param?.seriesData && seriesRef.current) {
                const main = param.seriesData.get?.(seriesRef.current);
                const t = main && typeof main.time === 'number' ? main.time : null;
                if (t != null) {
                    const maybe = timeToIndexRef.current.get(t);
                    if (maybe != null) idx = maybe;
                }
            }
            if (idx != null) {
                try { onBarSelectedRef.current && onBarSelectedRef.current(idx); } catch { /* ignore */ }
            }
        };
    try { chart.subscribeClick(clickHandler); } catch { /* ignore */ }

        const applyResize = () => {
            const { width, height: h2 } = computeSize();
            chart.applyOptions({ width, height: h2 });
        };

        const resizeTimers = [];
        const resizeFrames = [];
        const scheduleResize = (delay) => {
            const run = () => {
                const frame = requestAnimationFrame(applyResize);
                resizeFrames.push(frame);
            };
            if (delay === 0) {
                run();
                return;
            }
            resizeTimers.push(window.setTimeout(run, delay));
        };
        CHART_INITIAL_RESIZE_DELAYS_MS.forEach(scheduleResize);

        // Observe container size changes
        let ro;
        try {
            ro = new ResizeObserver(() => applyResize());
            ro.observe(el);
    } catch { /* ignore */ }

    // Fallback to window resize
    window.addEventListener('resize', applyResize);

        return () => {
            window.removeEventListener('resize', applyResize);
            resizeTimers.forEach((timer) => window.clearTimeout(timer));
            resizeFrames.forEach((frame) => cancelAnimationFrame(frame));
            try { ro && ro.disconnect(); } catch { /* ignore */ }
            try { if (clickHandler) chart.unsubscribeClick(clickHandler); } catch { /* ignore */ }
            if (chartRef.current) {
                try {
                    historicalTradeMarkersRef.current?.setMarkers([]);
                    historicalTradeConnectorSeriesRef.current.forEach((series) => {
                        try { chartRef.current.removeSeries(series); } catch { /* ignore */ }
                    });
                    historicalTradeConnectorSeriesRef.current = [];
                    historicalTradeMarkersRef.current = null;
                } catch { /* ignore */ }
                try {
                    if (volumeSeriesRef.current) {
                        chartRef.current.removeSeries(volumeSeriesRef.current);
                        volumeSeriesRef.current = null;
                    }
                } catch { /* ignore */ }
                // Remove support/resistance price lines
                try {
                    if (seriesRef.current && supportResistanceLinesRef.current.size > 0) {
                        supportResistanceLinesRef.current.forEach((lines) => {
                            lines.forEach(line => {
                                try { seriesRef.current.removePriceLine(line); } catch { /* ignore */ }
                            });
                        });
                        supportResistanceLinesRef.current.clear();
                    }
                } catch { /* ignore */ }
                chartRef.current.remove();
                if (chartRef.current === chart) chartRef.current = null;
                if (seriesRef.current) seriesRef.current = null;
            }
        };
    }, [height, prefs?.locale, axisFmtFn, crosshairTimeFmt]); // re-init if height/locale/format changes

    // ── Drawing tools: attach / detach manager on chart (re)init ─────────────
    useEffect(() => {
        const chart = chartRef.current;
        const series = seriesRef.current;
        const canvas = drawingCanvasRef.current;
        if (!chart || !series || !canvas) return;

        if (!drawingManagerRef.current) {
            drawingManagerRef.current = new DrawingToolsManager();
        }
        const mgr = drawingManagerRef.current;
        mgr.onChange = () => {
            setDrawingSelection(!!mgr._selectedId);
            setDrawingCount(mgr.drawings.length);
            setDrawings([...mgr.drawings]);
            setSelectedDrawingId(mgr._selectedId || null);
        };
        mgr.attach(canvas, chart, series, selectedSymbol, selectedTimeframe);
        setDrawingCount(mgr.drawings.length);
        setDrawings([...mgr.drawings]);

        return () => {
            mgr.detach();
        };
    }, [height, prefs?.locale, axisFmtFn, crosshairTimeFmt, selectedSymbol, selectedTimeframe]);

    // Keep drawing manager in sync with tool selection
    useEffect(() => {
        drawingManagerRef.current?.setTool(activeDrawingTool);
    }, [activeDrawingTool]);

    // Keep drawing manager in sync with symbol/timeframe changes
    useEffect(() => {
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setContext(selectedSymbol, selectedTimeframe);
            setDrawingCount(drawingManagerRef.current.drawings.length);
            setDrawings([...drawingManagerRef.current.drawings]);
        }
    }, [selectedSymbol, selectedTimeframe]);

    // Expose a way to re-render drawings when chart scrolls/zooms
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const handler = () => drawingManagerRef.current?.markDirty();
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); } catch { /* ignore */ }
        return () => {
            try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch { /* ignore */ }
        };
    }, [height, prefs?.locale, axisFmtFn, crosshairTimeFmt]);

    const handleSelectDrawingTool = useCallback((tool) => {
        setActiveDrawingTool(tool);
    }, []);

    const handleDeleteSelectedDrawing = useCallback(() => {
        drawingManagerRef.current?.deleteSelected();
        setDrawingSelection(false);
        setDrawingCount(drawingManagerRef.current?.drawings.length || 0);
    }, []);

    const handleDeleteAllDrawings = useCallback(() => {
        drawingManagerRef.current?.deleteAll();
        setDrawingSelection(false);
        setDrawingCount(0);
        setDrawings([]);
        setSelectedDrawingId(null);
    }, []);

    const handleUpdateDrawing = useCallback((id, props) => {
        drawingManagerRef.current?.updateDrawing(id, props);
        setDrawings([...(drawingManagerRef.current?.drawings || [])]);
    }, []);

    const handleSelectDrawing = useCallback((id) => {
        drawingManagerRef.current?.selectDrawing(id);
        setSelectedDrawingId(id);
        setDrawingSelection(!!id);
    }, []);

    // Measure pane tops relative to container by looking for canvases stacks
    const recomputePaneTops = () => {
        const host = chartContainerRef.current;
        if (!host) return;
        // Collect top offsets of immediate pane wrappers by locating their first canvas
        const canvases = Array.from(host.querySelectorAll('canvas'));
        // Heuristic: pick unique top offsets grouped per top-level pane area. Sort ascending.
        const offsets = [];
        const seen = new Set();
        canvases.forEach((cv) => {
            const rect = cv.getBoundingClientRect();
            const hostRect = host.getBoundingClientRect();
            const top = Math.round(rect.top - hostRect.top);
            if (top >= 0 && top < host.clientHeight) {
                const key = `${top}`;
                if (!seen.has(key)) { seen.add(key); offsets.push(top); }
            }
        });
        offsets.sort((a,b) => a - b);
        // Map to paneIndex: 0 is main; next are panes in order
        const map = [];
        offsets.forEach((t, idx) => { map[idx] = t; });
        setPaneTops(map);
    };

    // Keep pane positions updated
    useEffect(() => {
        const host = chartContainerRef.current;
        if (!host || typeof MutationObserver === 'undefined') return;
        const obs = new MutationObserver(() => recomputePaneTops());
        obs.observe(host, { childList: true, subtree: true, attributes: true });
        // Also on window resize
        const onResize = () => recomputePaneTops();
        window.addEventListener('resize', onResize);
        // Initial compute
        setTimeout(() => recomputePaneTops(), 0);
        return () => {
            try { obs.disconnect(); } catch { /* ignore */ }
            window.removeEventListener('resize', onResize);
        };
    }, []);

    // Lazy-load older candles on left scroll edge
    useEffect(() => {
        if (!loadMoreHistory) return;
        const chart = chartRef.current;
        if (!chart || !seriesRef.current) return;

        let detach = null;
        let debouncing = false;
        const timeScale = chart.timeScale();

        const ensureLeftData = async () => {
            if (debouncing) return;
            debouncing = true;
            try {
                // Snapshot current left bar index and from/to
                const range = timeScale.getVisibleLogicalRange?.();
                const beforeBars = barsRef.current || [];
                const beforeDataLen = beforeBars.length;
                const restoreCandidate = buildViewportRestoreCandidate(range, beforeBars);
                if (restoreCandidate) {
                    pendingScrollRestoreRef.current = restoreCandidate;
                    isRestoringScrollRef.current = true;
                    suppressAutoCenterUntilRef.current = Date.now() + 5000;
                    didCenterRef.current = true;
                    if (pendingAutoCenterTimerRef.current) {
                        clearTimeout(pendingAutoCenterTimerRef.current);
                        pendingAutoCenterTimerRef.current = null;
                    }
                }
                const res = await (loadMoreHistory ? loadMoreHistory() : Promise.resolve({ added: 0, total: beforeDataLen }));
                const added = Number(res?.added) || 0;
                if (added <= 0 && pendingScrollRestoreRef.current === restoreCandidate) {
                    pendingScrollRestoreRef.current = null;
                    isRestoringScrollRef.current = false;
                }
            } finally {
                // small delay to avoid thrashing
                setTimeout(() => { debouncing = false; }, 200);
            }
        };

        const onRangeChange = () => {
            try {
                const range = timeScale.getVisibleLogicalRange?.();
                if (!range) return;
                const from = range.from;
                const barsLoaded = barsRef.current?.length || 0;
                const leftLoadThreshold = Math.max(10, Math.min(50, Math.ceil(barsLoaded * 0.03)));
                // When user scrolls near the first bar, range.from approaches 0.
                if (typeof from === 'number' && from < leftLoadThreshold && barsLoaded >= 1) {
                    ensureLeftData();
                }
            } catch { /* ignore */ }
        };

        try {
            timeScale.subscribeVisibleLogicalRangeChange(onRangeChange);
            window.setTimeout(onRangeChange, 0);
            detach = () => timeScale.unsubscribeVisibleLogicalRangeChange(onRangeChange);
    } catch { /* ignore */ }

    return () => { try { detach && detach(); } catch { /* ignore */ } };
    }, [loadMoreHistory, height, prefs?.locale, axisFmtFn, crosshairTimeFmt]);

    // React to timezone/locale changes by updating chart localization
    useEffect(() => {
        if (!chartRef.current) return;
        chartRef.current.applyOptions({
            localization: { locale: prefs?.locale || 'en-US', timeFormatter: (t) => crosshairTimeFmt(t) },
            timeScale: { tickMarkFormatter: (t, tickType) => axisFmtFn(t, tickType) },
        });
    }, [prefs?.locale, prefs?.timezone, axisFmtFn, crosshairTimeFmt]);

    // Helper to lazily create/remove the volume histogram series
    const ensureVolumeSeries = useCallback(() => {
        if (!showVolume) return null;
        const chart = chartRef.current;
        if (!chart) return null;
        // Assign a dedicated pane index for volume if not yet set
        if (volumePaneIndexRef.current == null) {
            // Reserve pane index 1 for volume; existing pane indicators (backtest) will start from 2
            volumePaneIndexRef.current = 1;
        }
        if (!volumeSeriesRef.current) {
            const paneIndex = volumePaneIndexRef.current;
            const vs = chart.addSeries(HistogramSeries, {
                priceScaleId: 'volume',
                priceFormat: { type: 'volume' },
                color: '#22C55E',
            }, paneIndex);
            // Try setting pane height modestly
            try { vs.getPane?.().setHeight?.(80); } catch { /* ignore */ }
            // Configure scale
            try {
                const volScale = chart.priceScale('volume');
                volScale?.applyOptions({ visible: false, borderVisible: false, scaleMargins: { top: 0.05, bottom: 0 } });
            } catch { /* ignore */ }
            volumeSeriesRef.current = vs;
        }
        return volumeSeriesRef.current;
    }, [showVolume]);
    const removeVolumeSeries = useCallback(() => {
        try {
            if (chartRef.current && volumeSeriesRef.current) {
                chartRef.current.removeSeries(volumeSeriesRef.current);
                volumeSeriesRef.current = null;
            }
    } catch { /* ignore */ }
    }, []);

    // Historical Data (Live or Backtest)
    useEffect(() => {
        if (!seriesRef.current) return;

        // When historical data changes (new symbol/timeframe), do not clear indicators —
        // we preserve and will reapply visual overrides via cache.

        const dataContextKey = isBacktest
            ? `backtest|${selectedTimeframe || ''}`
            : `${selectedAccount || ''}|${selectedSymbol || ''}|${selectedTimeframe || ''}`;
        const previousDataContextKey = lastDataContextKeyRef.current;
        const dataContextChanged = previousDataContextKey !== dataContextKey;
        lastDataContextKeyRef.current = dataContextKey;
        const currentBars = barsRef.current || [];
        const shouldPreserveCurrentViewport = !dataContextChanged
            && !props?.selectedTrade
            && !pendingScrollRestoreRef.current
            && Array.isArray(currentBars)
            && currentBars.length > 0
            && chartRef.current;
        const currentViewportRestore = shouldPreserveCurrentViewport
            ? buildViewportRestoreCandidate(
                chartRef.current.timeScale().getVisibleLogicalRange?.(),
                currentBars
            )
            : null;
        if (currentViewportRestore) {
            isRestoringScrollRef.current = true;
            suppressAutoCenterUntilRef.current = Date.now() + 3000;
        }

        const dataToUse = isBacktest ? convertOhlcvToBars(ohlcvData) : historicalData;

        // For candlestick series, ensure we never feed null/NaN OHLC; use whitespace bars instead
        const sanitized = Array.isArray(dataToUse)
            ? dataToUse.map(b => {
                const o = Number(b?.open), h = Number(b?.high), l = Number(b?.low), c = Number(b?.close);
                const t = b?.time;
                const allFinite = [o, h, l, c].every(Number.isFinite);
                if (typeof t !== 'number' || !allFinite) return null;
                return {
                    time: t,
                    ...(b?.time_real != null ? { time_real: normalizeChartTime(b.time_real) } : {}),
                    open: o,
                    high: h,
                    low: l,
                    close: c,
                };
            }).filter(Boolean)
            : [];

        // Deduplicate by timestamp - lightweight-charts requires strictly ascending time.
        // Keep the last bar for each timestamp (most recent update wins).
        const deduped = deduplicateData(sanitized);

        if (deduped.length === 0) {
            seriesRef.current.setData([]); // Clear data if no data
            // clear refs
            barsRef.current = [];
            timeToIndexRef.current = new Map();
            isRestoringScrollRef.current = false;
            // Also clear volume if present
            removeVolumeSeries();
            return;
        }

        seriesRef.current.setData(deduped);

        // Restore scroll position after backfill prepend or any same-context data replacement.
        const restoreCandidate = pendingScrollRestoreRef.current || currentViewportRestore;
        if (restoreCandidate && chartRef.current) {
            const { from, to, oldLen, anchorOffset = 0, anchorTime } = restoreCandidate;
            pendingScrollRestoreRef.current = null;
            const newAnchorIndex = findBarIndexByAnchorTime(deduped, anchorTime);
            const rangeSpan = to - from;
            if (newAnchorIndex >= 0 && Number.isFinite(rangeSpan) && rangeSpan > 0) {
                const nextFrom = newAnchorIndex + anchorOffset;
                const nextRange = { from: nextFrom, to: nextFrom + rangeSpan };
                const ts = chartRef.current.timeScale();
                isRestoringScrollRef.current = true;
                const applyRestore = () => {
                    try { ts.setVisibleLogicalRange(nextRange); } catch { /* ignore */ }
                };
                requestAnimationFrame(() => {
                    applyRestore();
                    requestAnimationFrame(() => {
                        applyRestore();
                        isRestoringScrollRef.current = false;
                        suppressAutoCenterUntilRef.current = Date.now() + 1000;
                    });
                });
            } else {
                // Fallback to length shift when the anchor bar cannot be matched.
                const shift = deduped.length - oldLen;
                if (shift > 0) {
                    const ts = chartRef.current.timeScale();
                    isRestoringScrollRef.current = true;
                    requestAnimationFrame(() => {
                        try {
                            ts.setVisibleLogicalRange({ from: from + shift, to: to + shift });
                        } catch { /* ignore */ }
                        isRestoringScrollRef.current = false;
                        suppressAutoCenterUntilRef.current = Date.now() + 1000;
                    });
                } else {
                    isRestoringScrollRef.current = false;
                }
            }
        }

        // cache for quick lookup
        barsRef.current = deduped;
        const map = new Map();
        for (let i = 0; i < deduped.length; i++) {
            const t = deduped[i]?.time;
            if (typeof t === 'number') map.set(t, i);
        }
        timeToIndexRef.current = map;
        // chartRef.current.timeScale().fitContent();

        // Volume mapping: prefer explicit volume in source data when available
        const toSecondsSafe = (t) => (typeof t === 'number' ? (t > 1000000000000 ? Math.floor(t / 1000) : t) : undefined);
        let volumePoints = [];
        if (isBacktest && Array.isArray(ohlcvData)) {
            // Pull volume from backtest OHLCV if present. Accept several possible field names.
            volumePoints = ohlcvData
                .map(d => {
                    if (!d) return null;
                    const t = toSecondsSafe(d.time);
                    if (!Number.isFinite(t)) return null;
                    const rawVol = d.volume ?? d.vol ?? d.tick_volume ?? d.tickVolume;
                    if (!Number.isFinite(rawVol)) return null;
                    const isUp = Number(d.close) >= Number(d.open);
                    return { time: t, value: Number(rawVol), color: isUp ? '#22C55E' : '#EF4444' };
                })
                .filter(Boolean)
                .sort((a, b) => a.time - b.time);
        } else if (Array.isArray(historicalData) && historicalData.length > 0) {
            // Some backends may include volume on historicalData in the future
            const hasVol = historicalData.some(b => Number.isFinite(b?.volume) || Number.isFinite(b?.tickVolume));
            if (hasVol) {
                const map = new Map();
                sanitized.forEach((bar) => { if (typeof bar?.time === 'number') map.set(bar.time, bar); });
                volumePoints = historicalData
                    .map((b) => {
                        const t = b?.time;
                        const v = Number.isFinite(b?.volume) ? Number(b.volume) : (Number.isFinite(b?.tickVolume) ? Number(b.tickVolume) : undefined);
                        if (!Number.isFinite(v) || typeof t !== 'number') return null;
                        const bar = map.get(t);
                        const isUp = bar && Number.isFinite(bar?.open) && Number.isFinite(bar?.close) ? (bar.close >= bar.open) : true;
                        return { time: t, value: v, color: isUp ? '#22C55E' : '#EF4444' };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.time - b.time);
            }
        }

        if (showVolume && volumePoints.length > 0) {
            const vs = ensureVolumeSeries();
            try { vs.setData(volumePoints); } catch { /* ignore */ }
        } else {
            // No volume available; remove pane if exists (and any overlays)
            removeVolumeSeries();
            try {
                volumeOverlaySeriesRef.current.forEach((s) => { try { chartRef.current?.removeSeries(s); } catch { /* ignore */ } });
                volumeOverlaySeriesRef.current.clear();
                setBacktestVolumeOverlayLabels([]);
            } catch { /* ignore */ }
        }

    }, [
        historicalData,
        ohlcvData,
        isBacktest,
        showVolume,
        ensureVolumeSeries,
        removeVolumeSeries,
        selectedAccount,
        selectedSymbol,
        selectedTimeframe,
        props?.selectedTrade,
    ]);

    // Scroll to selected trade
    useEffect(() => {
        if (!chartRef.current || !selectedTrade) return;
    
        const markerBars = barsRef.current || [];
        const rawTradeTimeInSeconds = Math.floor(new Date(selectedTrade.entry_timestamp).getTime() / 1000);
        const tradeTimeInSeconds = snapTimeToContainingBar(rawTradeTimeInSeconds, markerBars);
        if (tradeTimeInSeconds === null) return;
        
        const barInterval = medianBarInterval(sortedBarTimes(markerBars));
        const windowInSeconds = Math.max(60 * 60, barInterval * 2);
        const from = tradeTimeInSeconds - windowInSeconds;
        const to = tradeTimeInSeconds + windowInSeconds;

        chartRef.current.timeScale().setVisibleRange({ from, to });

    }, [selectedTrade, ohlcvData, historicalData]);

    // Live updates (only for live trading)
    useEffect(() => {
        if (!isBacktest && seriesRef.current && candle) {
            // Guard against out-of-order or invalid time values
            const lastHistTime = historicalData?.[historicalData.length - 1]?.time;
            if (typeof candle.time !== 'number') return;
            if (typeof lastHistTime === 'number' && candle.time < lastHistTime) return;
            const o = Number(candle?.open), h = Number(candle?.high), l = Number(candle?.low), c = Number(candle?.close);
            const allFinite = [o, h, l, c].every(Number.isFinite);
            const payload = allFinite ? { time: candle.time, open: o, high: h, low: l, close: c } : { time: candle.time };
            seriesRef.current.update(payload);

            // Update volume if present on live candle
            const vol = Number.isFinite(candle?.volume) ? Number(candle.volume) : (Number.isFinite(candle?.tickVolume) ? Number(candle.tickVolume) : (Number.isFinite(candle?.tick_volume) ? Number(candle.tick_volume) : undefined));
        if (showVolume && Number.isFinite(vol)) {
                const vs = ensureVolumeSeries(0);
                const isUp = allFinite ? (c >= o) : true;
                try { vs.update({ time: candle.time, value: vol, color: isUp ? '#22C55E' : '#EF4444' }); } catch { /* ignore */ }
            }
            // Do not auto-center per tick; only center once after initial load/connection
        }
    }, [candle, isBacktest, historicalData, showVolume, ensureVolumeSeries]);

    // Live indicator point updates — lightweight .update() calls (like candle live updates)
    // This avoids the heavy full-render indicator useEffect on every tick.
    useEffect(() => {
        if (!indicatorLiveUpdate || !chartRef.current || isBacktest) return;
        const { id: frontendId, point, data_keys } = indicatorLiveUpdate;
        const realTime = point.time;

        // Find compressed time from historicalData (last or second-to-last bar)
        const last = historicalData[historicalData.length - 1];
        const secondToLast = historicalData.length >= 2 ? historicalData[historicalData.length - 2] : null;
        let compressedTime = realTime;
        if (last && (last.time_real ?? last.time) === realTime) {
            compressedTime = last.time;
        } else if (secondToLast && (secondToLast.time_real ?? secondToLast.time) === realTime) {
            compressedTime = secondToLast.time;
        }

        const currentSeries = indicatorSeriesRef.current;

        // Helper to get value for a key from the point data
        const valueFor = (k) => {
            if (!k) return undefined;
            if (Object.prototype.hasOwnProperty.call(point, k)) return point[k];
            if (point.value && typeof point.value === 'object' && Object.prototype.hasOwnProperty.call(point.value, k)) return point.value[k];
            if (point.outputs && typeof point.outputs === 'object' && Object.prototype.hasOwnProperty.call(point.outputs, k)) return point.outputs[k];
            return undefined;
        };

        if (Array.isArray(data_keys) && data_keys.length > 1) {
            // Multi-output: update each series
            data_keys.forEach(key => {
                const seriesId = `${frontendId}-${key}`;
                const entry = currentSeries.get(seriesId);
                if (!entry?.series) return;
                const v = valueFor(key);
                if (!Number.isFinite(v)) return;
                try { entry.series.update({ time: compressedTime, value: v }); } catch { /* ignore */ }
            });
        } else {
            // Single output
            let value = undefined;
            if (Array.isArray(data_keys) && data_keys.length > 0) {
                value = valueFor(data_keys[0]);
            }
            if (value == null && Number.isFinite(point.value)) value = point.value;
            if (value == null) {
                for (const k of Object.keys(point)) {
                    if (k === 'time') continue;
                    if (Number.isFinite(point[k])) { value = point[k]; break; }
                }
            }
            if (Number.isFinite(value)) {
                let entry = currentSeries.get(frontendId);
                if (!entry?.series && Array.isArray(data_keys) && data_keys[0]) {
                    entry = currentSeries.get(`${frontendId}-${data_keys[0]}`);
                }
                if (entry?.series) {
                    try { entry.series.update({ time: compressedTime, value }); } catch { /* ignore */ }
                }
            }
        }
    }, [indicatorLiveUpdate, isBacktest, historicalData]);

    // Center only once after initial load when WS is connected and history is present
    useEffect(() => {
        const key = `${selectedAccount || ''}|${selectedSymbol || ''}|${selectedTimeframe || ''}`;
        // Reset flag when identity changes
        if (centeredContextKeyRef.current !== key) {
            centeredContextKeyRef.current = key;
            didCenterRef.current = false;
            suppressAutoCenterUntilRef.current = 0;
            if (pendingAutoCenterTimerRef.current) {
                clearTimeout(pendingAutoCenterTimerRef.current);
                pendingAutoCenterTimerRef.current = null;
            }
        }
        if (!chartRef.current) return;
        if (props?.selectedTrade) return; // respect explicit trade focus
        if (pendingScrollRestoreRef.current || isRestoringScrollRef.current) return;
        if (Date.now() < suppressAutoCenterUntilRef.current) return;
        // Require both: connected socket and some history
        if (!priceFeed?.isConnected) return;
        const histLen = Array.isArray(historicalData) ? historicalData.length : 0;
        if (histLen <= 0) return;
        if (!didCenterRef.current) {
            // Slight delay to ensure any initial indicators/layout settle
            if (pendingAutoCenterTimerRef.current) {
                clearTimeout(pendingAutoCenterTimerRef.current);
            }
            pendingAutoCenterTimerRef.current = setTimeout(() => {
                pendingAutoCenterTimerRef.current = null;
                if (pendingScrollRestoreRef.current || isRestoringScrollRef.current) return;
                if (Date.now() < suppressAutoCenterUntilRef.current) return;
                centerOnLastBar();
                didCenterRef.current = true;
            }, 0);
            return () => {
                if (pendingAutoCenterTimerRef.current) {
                    clearTimeout(pendingAutoCenterTimerRef.current);
                    pendingAutoCenterTimerRef.current = null;
                }
            };
        }
    }, [priceFeed?.isConnected, historicalData, selectedAccount, selectedSymbol, selectedTimeframe, centerOnLastBar, props?.selectedTrade]);

    // Tick/pip/digits sizing -> controls price axis precision and step
    useEffect(() => {
        if (!seriesRef.current) return;

        // Parse numeric values safely
        const t = (tickSize != null && !Number.isNaN(Number(tickSize)) && Number(tickSize) > 0)
            ? Number(tickSize) : undefined;
        const p = (pipSize != null && !Number.isNaN(Number(pipSize)) && Number(pipSize) > 0)
            ? Number(pipSize) : undefined;
        // Lookup cached digits if prop not provided
        const cachedDigits = digits ?? getDigitsFor(selectedAccount, selectedSymbol);
        const hasDigits = Number.isFinite(cachedDigits);

        // Choose a reasonable minMove; prefer the smaller positive step
        const minMove = (t && p) ? Math.min(t, p) : (t || p);
        // If we have digits but no minMove known, we can still apply precision

        // Derive precision; prefer pipSize when available for better digit depth
        const basis = p || minMove;
        const getPrecision = (val) => {
            if (!Number.isFinite(val)) return 0;
            // Avoid issues with float repr; increment multiplier until integer or cap
            let e = 1, digits = 0;
            while (Math.round(val * e) / e !== val && digits < 10) {
                e *= 10;
                digits += 1;
            }
            return digits;
        };
        const precision = hasDigits ? Number(cachedDigits) : getPrecision(basis);

        // If this is a backtest and precision ended up 0 (or very low) but symbol looks like FX, derive standard FX digits
        let fxAdjustedPrecision = precision;
        if (isBacktest && (!Number.isFinite(fxAdjustedPrecision) || fxAdjustedPrecision < 2)) {
            const sym = (selectedSymbol || '').toUpperCase();
            // Basic heuristic: 6 chars alpha and not containing '/': treat as XXXYYY FX
            if (/^[A-Z]{6,7}$/.test(sym)) {
                // JPY pairs usually 3 decimals (0.001) for pip, some brokers 2; else 5 decimals typical
                if (sym.includes('JPY')) fxAdjustedPrecision = 3; else fxAdjustedPrecision = 5;
            } else if (sym.includes('/') && sym.replace('/', '').length === 6) {
                // Format like EUR/USD
                if (sym.includes('JPY')) fxAdjustedPrecision = 3; else fxAdjustedPrecision = 5;
            } else {
                // Fallback: infer from recent close price magnitude if available via barsRef
                try {
                    const last = barsRef.current && barsRef.current.length ? barsRef.current[barsRef.current.length - 1] : null;
                    const refPrice = last ? last.close : undefined;
                    if (Number.isFinite(refPrice)) {
                        if (refPrice < 0.1) fxAdjustedPrecision = 6; else if (refPrice < 1) fxAdjustedPrecision = 5; else if (refPrice < 10) fxAdjustedPrecision = 4; else fxAdjustedPrecision = 2;
                    }
                } catch { /* ignore */ }
            }
        }

        const priceFormat = { type: 'price', precision: fxAdjustedPrecision };
        if (Number.isFinite(minMove)) {
            priceFormat.minMove = minMove;
        } else if (hasDigits && Number.isFinite(fxAdjustedPrecision) && fxAdjustedPrecision >= 0 && fxAdjustedPrecision <= 12) {
            priceFormat.minMove = Math.pow(10, -fxAdjustedPrecision);
        } else if (isBacktest && Number.isFinite(fxAdjustedPrecision)) {
            priceFormat.minMove = Math.pow(10, -fxAdjustedPrecision);
        }
        seriesRef.current.applyOptions({ priceFormat });
    }, [tickSize, pipSize, digits, selectedAccount, selectedSymbol, isBacktest]);

    // Stop Loss Line (only for live trading)
    useEffect(() => {
        if (!chartRef.current || isBacktest) return;

        if (stopLossPrice && stopLossPrice !== 'N/A' && !isNaN(parseFloat(stopLossPrice))) {
            if (!stopLossLineRef.current) {
                stopLossLineRef.current = seriesRef.current.createPriceLine({
                    price: parseFloat(stopLossPrice),
                    color: '#EF4444', // Red for Stop Loss
                    lineWidth: 2,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: 'SL',
                });
            } else {
                stopLossLineRef.current.applyOptions({ price: parseFloat(stopLossPrice) });
            }
        } else {
            if (stopLossLineRef.current) {
                seriesRef.current.removePriceLine(stopLossLineRef.current);
                stopLossLineRef.current = null;
            }
        }
    }, [stopLossPrice, isBacktest]);

    // Take Profit Line (only for live trading)
    useEffect(() => {
        if (!chartRef.current || isBacktest) return;

        if (takeProfitPrice && takeProfitPrice !== 'N/A' && !isNaN(parseFloat(takeProfitPrice))) {
            if (!takeProfitLineRef.current) {
                takeProfitLineRef.current = seriesRef.current.createPriceLine({
                    price: parseFloat(takeProfitPrice),
                    color: '#22C55E', // Green for Take Profit
                    lineWidth: 2,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: 'TP',
                });
            } else {
                takeProfitLineRef.current.applyOptions({ price: parseFloat(takeProfitPrice) });
            }
        } else {
            if (takeProfitLineRef.current) {
                seriesRef.current.removePriceLine(takeProfitLineRef.current);
                takeProfitLineRef.current = null;
            }
        }
    }, [takeProfitPrice, isBacktest]);

    // Open position entry/SL/TP lines (live chart "Trades" overlay)
    useEffect(() => {
        const mainSeries = seriesRef.current;
        if (!mainSeries || isBacktest) {
            openPositionLinesRef.current = [];
            return;
        }

        openPositionLinesRef.current.forEach((line) => {
            try { mainSeries.removePriceLine(line); } catch { /* ignore */ }
        });
        openPositionLinesRef.current = [];

        if (!showRecentTrades) return;

        const specs = getOpenPositionOverlaySpecs(openPositions, selectedSymbol);
        const createdLines = [];

        specs.forEach((spec) => {
            try {
                const isEntry = spec.kind === 'entry';
                createdLines.push(mainSeries.createPriceLine({
                    price: spec.price,
                    color: spec.color,
                    lineWidth: isEntry ? 2 : 1,
                    lineStyle: isEntry ? LineStyle.Solid : LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: spec.title,
                }));
            } catch (err) {
                console.error('Failed to create open position price line:', err);
            }
        });

        openPositionLinesRef.current = createdLines;

        return () => {
            createdLines.forEach((line) => {
                try { mainSeries.removePriceLine(line); } catch { /* ignore */ }
            });
            if (openPositionLinesRef.current === createdLines) {
                openPositionLinesRef.current = [];
            }
        };
    }, [openPositions, selectedAccount, selectedSymbol, selectedTimeframe, showRecentTrades, isBacktest]);

    // Support/Resistance Price Lines
    useEffect(() => {
        if (!chartRef.current || !seriesRef.current || isBacktest) return;
        
        const mainSeries = seriesRef.current;
        
        // Find all support_resistance indicators (regardless of paneType)
        const srIndicators = activeIndicators.filter(
            ind => {
                const name = String(ind?.name || '').toLowerCase().replace(/[_\s-]/g, '');
                return name === 'supportresistance' || name.includes('supportresistance');
            }
        );
        
        console.log('[SR] All active indicators:', activeIndicators.map(i => ({ name: i?.name, paneType: i?.paneType, id: i?.id })));
        console.log('[SR] Found support_resistance indicators:', srIndicators.length, srIndicators.map(i => ({ id: i.id, paneType: i.paneType })));
        
        // Track which indicators are active
        const activeIds = new Set(srIndicators.map(ind => ind.id));
        
        // Remove price lines for indicators that are no longer active
        supportResistanceLinesRef.current.forEach((lines, indicatorId) => {
            if (!activeIds.has(indicatorId)) {
                lines.forEach(line => {
                    try { mainSeries.removePriceLine(line); } catch { /* ignore */ }
                });
                supportResistanceLinesRef.current.delete(indicatorId);
            }
        });
        
        // Process each active support_resistance indicator
        srIndicators.forEach(indicator => {
            console.log('[SR] Processing indicator:', indicator.id, 'data:', indicator.data);
            
            if (!indicator.data) {
                console.log('[SR] No data for indicator:', indicator.id);
                return;
            }
            
            // Data can be either an object (single update) or array of objects
            let data = indicator.data;
            
            // If it's an array, get the latest data point
            if (Array.isArray(data)) {
                if (data.length === 0) {
                    console.log('[SR] Empty data array');
                    return;
                }
                data = data[data.length - 1];
            }
            
            // If the data point has nested structure, extract it
            if (data && typeof data === 'object' && !data.support_resistance_support_1) {
                // Check if data is in data.data or data.value or data.outputs
                if (data.data && typeof data.data === 'object') {
                    data = data.data;
                } else if (data.value && typeof data.value === 'object') {
                    data = data.value;
                } else if (data.outputs && typeof data.outputs === 'object') {
                    data = data.outputs;
                }
            }
            
            console.log('[SR] Extracted data:', data);
            
            // Helper function to get the latest value from an array or return the value as-is
            const getLatestValue = (val) => {
                if (Array.isArray(val) && val.length > 0) {
                    // Get the last non-null/non-undefined value
                    for (let i = val.length - 1; i >= 0; i--) {
                        const item = val[i];
                        if (item == null) continue;
                        // If it's an object with a value property, extract it
                        if (typeof item === 'object' && item.value != null) {
                            return item.value;
                        }
                        // If it's already a scalar, return it
                        if (typeof item === 'number') {
                            return item;
                        }
                    }
                    return null;
                }
                return val;
            };
            
            // Extract support and resistance levels
            const levels = [];
            
            // Support levels (1-5)
            for (let i = 1; i <= 5; i++) {
                const priceKey = `support_resistance_support_${i}`;
                const touchesKey = `support_resistance_support_touches_${i}`;
                const strengthKey = `support_resistance_support_strength_${i}`;
                
                const priceRaw = data[priceKey];
                const touchesRaw = data[touchesKey];
                const strengthRaw = data[strengthKey];
                
                const price = getLatestValue(priceRaw);
                const touches = getLatestValue(touchesRaw);
                const strength = getLatestValue(strengthRaw);
                
                console.log(`[SR] Support ${i}: priceRaw=${Array.isArray(priceRaw) ? `Array(${priceRaw.length})` : priceRaw}, price=${price}, touches=${touches}, strength=${strength}`);
                
                if (price != null && Number.isFinite(price)) {
                    levels.push({
                        type: 'support',
                        price,
                        touches: touches || 0,
                        strength: strength || 0,
                        index: i
                    });
                }
            }
            
            // Resistance levels (1-5)
            for (let i = 1; i <= 5; i++) {
                const priceKey = `support_resistance_resistance_${i}`;
                const touchesKey = `support_resistance_resistance_touches_${i}`;
                const strengthKey = `support_resistance_resistance_strength_${i}`;
                
                const priceRaw = data[priceKey];
                const touchesRaw = data[touchesKey];
                const strengthRaw = data[strengthKey];
                
                const price = getLatestValue(priceRaw);
                const touches = getLatestValue(touchesRaw);
                const strength = getLatestValue(strengthRaw);
                
                console.log(`[SR] Resistance ${i}: priceRaw=${Array.isArray(priceRaw) ? `Array(${priceRaw.length})` : priceRaw}, price=${price}, touches=${touches}, strength=${strength}`);
                
                if (price != null && Number.isFinite(price)) {
                    levels.push({
                        type: 'resistance',
                        price,
                        touches: touches || 0,
                        strength: strength || 0,
                        index: i
                    });
                }
            }
            
            console.log('[SR] Total levels found:', levels.length, levels);
            
            // Remove existing lines for this indicator
            const existingLines = supportResistanceLinesRef.current.get(indicator.id) || [];
            existingLines.forEach(line => {
                try { mainSeries.removePriceLine(line); } catch { /* ignore */ }
            });
            
            // Create new price lines
            const newLines = [];
            levels.forEach(level => {
                try {
                    // Color based on type
                    const baseColor = level.type === 'support' ? '#22C55E' : '#EF4444'; // Green for support, red for resistance
                    
                    // Optional: make lines slightly transparent
                    const lineWidth = 1;
                    const lineStyle = LineStyle.Solid;
                    
                    console.log(`[SR] Creating price line for ${level.type} ${level.index} at price ${level.price}`);
                    
                    const priceLine = mainSeries.createPriceLine({
                        price: level.price,
                        color: baseColor,
                        lineWidth: lineWidth,
                        lineStyle: lineStyle,
                        axisLabelVisible: true,
                        title: `${level.type === 'support' ? 'S' : 'R'}${level.index} (${level.touches})`,
                    });
                    
                    newLines.push(priceLine);
                    console.log(`[SR] Successfully created price line for ${level.type} ${level.index}`);
                } catch (err) {
                    console.error(`[SR] Failed to create price line for ${level.type}_${level.index}:`, err);
                }
            });
            
            console.log('[SR] Created', newLines.length, 'price lines for indicator', indicator.id);
            supportResistanceLinesRef.current.set(indicator.id, newLines);
        });
        
    }, [activeIndicators, isBacktest]);

    // Trade Markers (for backtesting) + optional extra markers (e.g., partial TPs)
    useEffect(() => {
        if (!seriesRef.current || !isBacktest) return;

        if (!markersRef.current) {
            markersRef.current = createSeriesMarkers(seriesRef.current);
        }
        const markerBars = barsRef.current || [];
        const base = convertMarkers(tradeMarkers, markerBars);
        const extras = Array.isArray(extraMarkers)
            ? extraMarkers
                .filter(m => m && typeof m.time === 'number')
                .map(m => ({ ...m, time: snapTimeToContainingBar(m.time, markerBars) }))
                .filter(m => typeof m.time === 'number')
            : [];
        // Strip price from partial TP marker labels like "TP2 @ 3563.68…" → "TP2"
        const extrasSanitized = extras.map((m) => {
            if (!m || typeof m.text !== 'string') return m;
            const txt = m.text;
            // Heuristic: only adjust labels that look like TP markers (TP, TP1, TP2, etc.)
            const isTp = /^\s*TP\s*\d*/i.test(txt);
            if (!isTp) return m;
            let newText = txt
                // Remove anything starting with '@' to the end
                .replace(/\s*@\s*.*/, '')
                // Or patterns like "TP1 at 1234.56" → remove from " at " to end
                .replace(/\s+at\s+.*/i, '')
                .trim();
            // Avoid empty labels
            if (!newText) newText = txt;
            return { ...m, text: newText };
        });
        const merged = [...base, ...extrasSanitized].sort((a, b) => a.time - b.time);
        try {
            markersRef.current.setMarkers(merged);
        } catch {
            try { markersRef.current.setMarkers(base); } catch { /* ignore */ }
        }
    }, [tradeMarkers, extraMarkers, isBacktest, ohlcvData, historicalData]);

    // Closed trade history fetch (live chart "Trades" overlay)
    useEffect(() => {
        if (isBacktest || !showRecentTrades || !selectedAccount || !selectedSymbol) {
            setHistoricalTradesForChart([]);
            return;
        }

        let aborted = false;

        (async () => {
            try {
                const result = await getAccountTrades(selectedAccount, {
                    instrument: selectedSymbol,
                    page_size: 100,
                });
                if (aborted) return;
                const trades = Array.isArray(result) ? result : (result?.results || []);
                setHistoricalTradesForChart(trades);
            } catch (error) {
                console.error('Failed to fetch historical trades for chart markers:', error);
                if (!aborted) setHistoricalTradesForChart([]);
            }
        })();

        return () => { aborted = true; };
    }, [showRecentTrades, selectedAccount, selectedSymbol, isBacktest]);

    // Historical trade entry/exit markers and connector lines (live chart "Trades" overlay)
    useEffect(() => {
        if (isBacktest) {
            clearHistoricalTradeOverlay();
            return;
        }

        const chart = chartRef.current;
        const mainSeries = seriesRef.current;
        if (!chart || !mainSeries || !showRecentTrades || !selectedAccount || !selectedSymbol) {
            clearHistoricalTradeOverlay();
            return;
        }

        if (!historicalTradeMarkersRef.current) {
            historicalTradeMarkersRef.current = createSeriesMarkers(mainSeries, [], { zOrder: 'top' });
        }

        const markerBars = barsRef.current || [];
        const historicalOverlay = buildHistoricalTradeOverlay(
            historicalTradesForChart,
            selectedSymbol,
            markerBars,
            selectedAccount,
            { maxTrades: 16 }
        );
        const openPositionOverlay = buildOpenPositionTradeOverlay(
            openPositions,
            selectedSymbol,
            markerBars,
            selectedAccount
        );
        const combinedMarkers = [
            ...historicalOverlay.markers,
            ...openPositionOverlay.markers,
        ].sort((a, b) => (a.time ?? 0) - (b.time ?? 0));
        const combinedConnectors = [
            ...historicalOverlay.connectors,
            ...openPositionOverlay.connectors,
        ];

        clearHistoricalTradeOverlay();

        try {
            historicalTradeMarkersRef.current?.setMarkers(combinedMarkers);
        } catch {
            try { historicalTradeMarkersRef.current?.setMarkers([]); } catch { /* ignore */ }
        }

        const connectorSeries = [];
        combinedConnectors.forEach((connector) => {
            try {
                const series = chart.addSeries(LineSeries, {
                    color: connector.color,
                    lineWidth: 2,
                    lineStyle: connector.lineStyle === 'dashed' ? LineStyle.Dashed : LineStyle.Solid,
                    priceLineVisible: false,
                    lastValueVisible: false,
                    crosshairMarkerVisible: false,
                    title: '',
                });
                series.setData(connector.data);
                connectorSeries.push(series);
            } catch (error) {
                console.error('Failed to draw historical trade connector:', error);
            }
        });
        historicalTradeConnectorSeriesRef.current = connectorSeries;

        return () => {
            connectorSeries.forEach((series) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            if (historicalTradeConnectorSeriesRef.current === connectorSeries) {
                historicalTradeConnectorSeriesRef.current = [];
            }
        };
    }, [
        clearHistoricalTradeOverlay,
        historicalTradesForChart,
        openPositions,
        selectedAccount,
        selectedSymbol,
        selectedTimeframe,
        showRecentTrades,
        isBacktest,
        historicalData,
    ]);

    // Track simple pane labels for indicators
    const [paneLabels, setPaneLabels] = useState([]); // [{paneIndex, text}]
    // Track overlay indicator labels (shown on main pane top-left)
    const [overlayLabels, setOverlayLabels] = useState([]); // [{id, text}]
    // Track measured pane top offsets inside container to place chips accurately
    const [paneTops, setPaneTops] = useState([]); // index -> top px
    // Manage per-pane chip portal roots
    // const paneChipRootsRef = useRef(new Map()); // paneIndex -> { mountEl, root }
    // const paneMutationObserverRef = useRef(null);

    // Pane and Overlay indicators (Live)
    useEffect(() => {
        if (!chartRef.current || isBacktest) return;

        const currentSeries = indicatorSeriesRef.current;
        const requiredSeriesIds = new Set();

        // Build a stable pane assignment for this render so each pane-type indicator
        // gets its own pane index consistently, regardless of whether it already exists.
    const paneAssignment = new Map();
    // Reserve the volume pane (index 1) when volume is shown, so new pane indicators start after it
    const reservedVolumePane = showVolume ? (volumePaneIndexRef.current ?? 1) : null;
    // Indicators in panes start at index 1 (0 is main price pane), or after reserved volume pane
    let nextPane = (reservedVolumePane != null) ? (reservedVolumePane + 1) : 1;
        activeIndicators.forEach((indicator) => {
            if (indicator?.paneType === 'pane') {
                paneAssignment.set(indicator.id, nextPane++);
            }
        });

        // First, determine all series that should be on the chart
        activeIndicators.forEach(indicator => {
            // Do not render an indicator explicitly named 'Price'
            if (String(indicator?.name || '').trim().toLowerCase() === 'price') return;
            // Skip support_resistance as it's handled with price lines
            if (String(indicator?.name || '').trim().toLowerCase() === 'support_resistance') return;
            if (Array.isArray(indicator.data)) {
                const indicatorMeta = getIndicatorMeta(availableIndicators, indicator?.name);
                const outKeys = getOutputKeys(indicator, indicatorMeta);
                if (outKeys.length > 1) {
                    const visualsByKey = (deepMerge(indicatorMeta?.visual_defaults || {}, indicator?.visual_overrides || {})?.series) || {};
                    const allowedSlope = chooseAllowedSlopeKeys(outKeys, indicator.params, visualsByKey);
                    outKeys.forEach((key) => {
                        if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return;
                        const seriesId = `${indicator.id}-${key}`;
                        requiredSeriesIds.add(seriesId);
                    });
                } else {
                    requiredSeriesIds.add(indicator.id);
                }
            } else if (typeof indicator.data === 'object' && indicator.data) {
                const allKeys = Object.keys(indicator.data);
                const indicatorMeta = getIndicatorMeta(availableIndicators, indicator?.name);
                const visualsByKey = (deepMerge(indicatorMeta?.visual_defaults || {}, indicator?.visual_overrides || {})?.series) || {};
                const allowedSlope = chooseAllowedSlopeKeys(allKeys, indicator.params, visualsByKey);
                allKeys.forEach(key => {
                    if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return;
                    const seriesId = `${indicator.id}-${key}`;
                    requiredSeriesIds.add(seriesId);
                });
            }
        });

        // Remove old series that are no longer required
        // Important: don't remove panes inline while iterating (pane indices can shift).
        // Collect candidate panes, then remove them in descending index order.
        const candidatePanesForRemoval = new Set();
        currentSeries.forEach((entry, id) => {
            if (!requiredSeriesIds.has(id)) {
                const chart = chartRef.current;
                if (chart) {
                    const paneRef = entry?.pane;
                    try {
                        if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                            try { chart.removeSeries(entry.series.posSeries); } catch { /* ignore */ }
                            try { chart.removeSeries(entry.series.negSeries); } catch { /* ignore */ }
                        } else {
                            chart.removeSeries(entry.series);
                        }
                    } catch { /* ignore */ }
                    if (paneRef) candidatePanesForRemoval.add(paneRef);
                }
                currentSeries.delete(id);
            }
        });

        // After removing series, clean up any now-empty panes by checking tracked usage
        if (candidatePanesForRemoval.size > 0 && chartRef.current) {
            try {
                const usedPaneIdx = new Set();
                currentSeries.forEach((entry) => {
                    const idx = entry?.pane && entry.pane.paneIndex ? entry.pane.paneIndex() : undefined;
                    if (typeof idx === 'number') usedPaneIdx.add(idx);
                });
                const indices = Array.from(candidatePanesForRemoval)
                    .map(p => (p && typeof p.paneIndex === 'function') ? p.paneIndex() : undefined)
                    .filter(i => {
                        if (!(typeof i === 'number' && i > 0)) return false;
                        if (typeof reservedVolumePane === 'number' && i === reservedVolumePane) return false;
                        return !usedPaneIdx.has(i);
                    });
                indices.sort((a, b) => b - a).forEach(idx => {
                    try { chartRef.current.removePane(idx); } catch { /* ignore */ }
                });
            } catch { /* ignore */ }
        }

        // Add or update series
        const newPaneLabels = [];
        const newOverlayLabels = [];

        // Count overlay indicators by name to know when to disambiguate with params
        const overlayCounts = Object.create(null);
    activeIndicators
            .filter((i) => i && i.paneType !== 'pane')
            .forEach((i) => {
        if (String(i?.name || '').trim().toLowerCase() === 'price') return;
                const k = String(i.name || '').toLowerCase();
                overlayCounts[k] = (overlayCounts[k] || 0) + 1;
            });
        // Count pane indicators by name as well
        const paneCounts = Object.create(null);
    activeIndicators
            .filter((i) => i && i.paneType === 'pane')
            .forEach((i) => {
        if (String(i?.name || '').trim().toLowerCase() === 'price') return;
                const k = String(i.name || '').toLowerCase();
                paneCounts[k] = (paneCounts[k] || 0) + 1;
            });
        activeIndicators.forEach(indicator => {
            // Skip drawing the 'Price' indicator altogether
            if (String(indicator?.name || '').trim().toLowerCase() === 'price') return;
            // Skip support_resistance as it's handled separately with price lines
            if (String(indicator?.name || '').trim().toLowerCase() === 'support_resistance') return;
            const isPaneIndicator = indicator.paneType === 'pane';
            const defaultPaneStart = (typeof reservedVolumePane === 'number') ? (reservedVolumePane + 1) : 1;
            const currentPaneIndex = isPaneIndicator ? (paneAssignment.get(indicator.id) ?? defaultPaneStart) : 0;
            if (isPaneIndicator) {
                const rawName = String(indicator.name || '').trim();
                if (rawName) {
                    const count = paneCounts[String(indicator.name || '').toLowerCase()] || 0;
                    const text = buildOverlayLabel(rawName, indicator.params, count > 1);
                    newPaneLabels.push({ paneIndex: currentPaneIndex, id: indicator.id, name: rawName, text });
                }
            } else {
                const rawName = String(indicator.name || '').trim();
                if (rawName) {
                    const count = overlayCounts[String(indicator.name || '').toLowerCase()] || 0;
                    const text = buildOverlayLabel(rawName, indicator.params, count > 1);
                    newOverlayLabels.push({ id: indicator.id, name: rawName, text });
                }
            }

            const indicatorMeta = getIndicatorMeta(availableIndicators, indicator?.name);
            const visualDefaults = indicatorMeta?.visual_defaults || {};
            const overrides = indicator?.visual_overrides || {};
            const effectiveVisuals = deepMerge(visualDefaults, overrides);
            const visualsByKey = effectiveVisuals?.series || {};

            const outKeys = getOutputKeys(indicator, indicatorMeta);

            if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
                try {
                    const sample = Array.isArray(indicator.data) ? indicator.data.slice(0, 3) : indicator.data;
                    console.log('[DMI] outKeys:', outKeys, 'paneType:', indicator.paneType, 'sample:', sample);
                } catch { /* ignore */ }
            }

            // Case 0: Multi-output provided as array-of-objects with multiple keys
            if (Array.isArray(indicator.data) && outKeys.length > 1) {
                const allowedSlope = chooseAllowedSlopeKeys(outKeys, indicator.params, visualsByKey);
                outKeys.forEach((key) => {
                    if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return; // skip unselected slope output
                    const seriesId = `${indicator.id}-${key}`;
                    const normalized = normalizeSeriesPoints(indicator.data, key);
                    const padded = padIndicatorData(historicalData, normalized);

                    const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                    const vForKey = picked.visual;
                    const fallbacks = {
                        color: indicator.params?.[`${key}_color`] || indicator.params?.color || '#FFD700',
                        lineWidth: indicator.params?.[`${key}_width`] || indicator.params?.width || 2,
                        lineStyle: indicator.params?.[`${key}_style`] || indicator.params?.style || LineStyle.Solid || 0,
                    };
                    const options = buildOptionsFromVisuals(vForKey, fallbacks);
                    // For slope outputs, render as a single line series (uniform color)
                    const isSlope = isSlopeOutputKey(key);
                    const plotTypePref = isSlope ? 'line' : (vForKey?.plotType || indicator.params?.[`${key}_plotType`] || indicator.params?.plotType || 'line');
                    const { SeriesCtor, type } = resolveSeriesType(plotTypePref);
                    const typeOpts = toSeriesTypeOptions(type, options);

                    // If this output is slope-like, ensure it gets its own pane
                    const desiredPaneIndex = (isSlope ? (paneAssignment.get(`${indicator.id}::${key}`) ?? (paneAssignment.set(`${indicator.id}::${key}`, nextPane++), paneAssignment.get(`${indicator.id}::${key}`))) : currentPaneIndex);

                    if (!currentSeries.has(seriesId)) {
                        // compute price format for pane indicators to avoid 0.00 for small values
                        const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                        const priceFormat = (isPaneIndicator || isSlope) ? derivePriceFormatFromPoints(pointsForPrecision, digits) : undefined;
                        const seriesOptions = {
                            ...typeOpts,
                            // Hide indicator name from price scale by omitting title
                        };
                        // For overlay indicators on the main pane, hide the dotted current-value line
                        if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                        if (isSlope) seriesOptions.visible = true;
                        if (priceFormat) seriesOptions.priceFormat = priceFormat;
                        const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                        currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                        if (isPaneIndicator || isSlope) { try { newSeries.getPane().setHeight(100); } catch { /* ignore */ } }
                    } else {
                        const entry = currentSeries.get(seriesId);
                        if (entry.plotType !== type) {
                            const pane = desiredPaneIndex;
                            // If prior render left a pair, remove both
                            try {
                                if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                    chartRef.current.removeSeries(entry.series.posSeries);
                                    chartRef.current.removeSeries(entry.series.negSeries);
                                } else {
                                    chartRef.current.removeSeries(entry.series);
                                }
                            } catch { /* ignore */ }
                            const so2 = { ...typeOpts };
                            if (!isPaneIndicator && !isSlope) so2.priceLineVisible = false;
                            if (isSlope) so2.visible = true;
                            const newSeries = chartRef.current.addSeries(SeriesCtor, so2, pane);
                            currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                        } else {
                            const seriesOptions = { ...typeOpts };
                            if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                            if (isPaneIndicator || isSlope) {
                                const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                                seriesOptions.priceFormat = derivePriceFormatFromPoints(pointsForPrecision, digits);
                            }
                            if (isSlope) seriesOptions.visible = true;
                            // If pane changed (e.g., volume pane got reserved), move series to desired pane
                            const actualPane = entry.pane?.paneIndex?.();
                            if ((isPaneIndicator || isSlope) && typeof actualPane === 'number' && actualPane !== desiredPaneIndex) {
                                try {
                                    if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                        chartRef.current.removeSeries(entry.series.posSeries);
                                        chartRef.current.removeSeries(entry.series.negSeries);
                                    } else {
                                        chartRef.current.removeSeries(entry.series);
                                    }
                                } catch { /* ignore */ }
                                const moved = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                                currentSeries.set(seriesId, { series: moved, pane: moved.getPane(), plotType: type });
                            } else {
                                entry.series.applyOptions(seriesOptions);
                            }
                        }
                    }

                    const entryNow = currentSeries.get(seriesId);
                    const hasValues = Array.isArray(padded) && padded.some(pt => Number.isFinite(pt?.value));
                    entryNow.series.setData(hasValues ? padded : normalized);
                });
                return; // handled this indicator
            }

            // Case 1: Single-value indicator (data is an array)
            if (Array.isArray(indicator.data) && indicator.data.length > 0) {
                const valueKey = outKeys[0] || (indicator.data_keys && indicator.data_keys.length > 0 ? indicator.data_keys[0] : 'value');
                const normalized = normalizeSeriesPoints(indicator.data, valueKey);
                const padded = padIndicatorData(historicalData, normalized);

                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, valueKey);
                const vForKey = picked.visual;
                const fallbacks = {
                    color: indicator.params?.color || '#FFD700',
                    lineWidth: indicator.params?.width || 2,
                    lineStyle: indicator.params?.style || LineStyle.Solid || 0,
                };
                const options = buildOptionsFromVisuals(vForKey, fallbacks);
                const isSlope = isSlopeOutputKey(valueKey);
                const plotTypePref = isSlope ? 'line' : (vForKey?.plotType || indicator.params?.plotType || 'line');
                const { SeriesCtor, type } = resolveSeriesType(plotTypePref);
                const typeOpts = toSeriesTypeOptions(type, options);
                const desiredPaneIndex = (isSlope ? (paneAssignment.get(`${indicator.id}::${valueKey}`) ?? (paneAssignment.set(`${indicator.id}::${valueKey}`, nextPane++), paneAssignment.get(`${indicator.id}::${valueKey}`))) : currentPaneIndex);

                if (!currentSeries.has(indicator.id)) {
                    const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                    const priceFormat = (isPaneIndicator || isSlope) ? derivePriceFormatFromPoints(pointsForPrecision, digits) : undefined;
                    const seriesOptions = { ...typeOpts };
                    if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                    if (priceFormat) seriesOptions.priceFormat = priceFormat;
                    if (isSlope) seriesOptions.visible = true;
                    const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                    currentSeries.set(indicator.id, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    if (isPaneIndicator || isSlope) { try { newSeries.getPane().setHeight(100); } catch { /* ignore */ } }
                } else {
                    const entry = currentSeries.get(indicator.id);
                    if (entry.plotType !== type) {
                        const pane = desiredPaneIndex;
                        try {
                            if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                chartRef.current.removeSeries(entry.series.posSeries);
                                chartRef.current.removeSeries(entry.series.negSeries);
                            } else {
                                chartRef.current.removeSeries(entry.series);
                            }
                        } catch { /* ignore */ }
                        const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                        const seriesOptions = { ...typeOpts };
                        if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                        if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(pointsForPrecision, digits);
                        if (isSlope) seriesOptions.visible = true;
                        const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, pane);
                        currentSeries.set(indicator.id, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    } else {
                        const seriesOptions = { ...typeOpts };
                        if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                        if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(padded, digits);
                        if (isSlope) seriesOptions.visible = true;
                        // If pane changed (e.g., reserve volume pane), move series
                        const actualPane = entry.pane?.paneIndex?.();
                        if ((isPaneIndicator || isSlope) && typeof actualPane === 'number' && actualPane !== desiredPaneIndex) {
                            try {
                                if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                    chartRef.current.removeSeries(entry.series.posSeries);
                                    chartRef.current.removeSeries(entry.series.negSeries);
                                } else {
                                    chartRef.current.removeSeries(entry.series);
                                }
                            } catch { /* ignore */ }
                            const moved = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                            currentSeries.set(indicator.id, { series: moved, pane: moved.getPane(), plotType: type });
                        } else {
                            entry.series.applyOptions(seriesOptions);
                        }
                    }
                }
                const entryNow = currentSeries.get(indicator.id);
                const hasValues1 = Array.isArray(padded) && padded.some(pt => Number.isFinite(pt?.value));
                entryNow.series.setData(hasValues1 ? padded : normalized);

            // Case 2: Multi-value indicator (data is an object)
            } else if (typeof indicator.data === 'object' && indicator.data) {
                const keysAll = Object.keys(indicator.data);
                const allowedSlope = chooseAllowedSlopeKeys(keysAll, indicator.params, visualsByKey);
                keysAll.forEach(key => {
                    if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return; // skip unselected slope output
                    const seriesId = `${indicator.id}-${key}`;
                    const seriesData = indicator.data[key];

                    if (seriesData && seriesData.length > 0) {
                        const normalized = normalizeSeriesPoints(seriesData, key);
                        if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
                            try { console.log('[DMI][norm]', key, normalized.slice(0, 3)); } catch { /* ignore */ }
                        }
                        const padded = padIndicatorData(historicalData, normalized);

                        const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                        const vForKey = picked.visual;
                        const fallbacks = {
                            color: indicator.params?.[`${key}_color`] || '#FFD700',
                            lineWidth: indicator.params?.[`${key}_width`] || 2,
                            lineStyle: indicator.params?.[`${key}_style`] || LineStyle.Solid || 0,
                        };
                        const options = buildOptionsFromVisuals(vForKey, fallbacks);
                        const isSlope = isSlopeOutputKey(key);
                        const plotTypePref = isSlope ? 'line' : (vForKey?.plotType || indicator.params?.[`${key}_plotType`] || 'line');
                        const { SeriesCtor, type } = resolveSeriesType(plotTypePref);
                        const typeOpts = toSeriesTypeOptions(type, options);
                        const desiredPaneIndex = (isSlope ? (paneAssignment.get(`${indicator.id}::${key}`) ?? (paneAssignment.set(`${indicator.id}::${key}`, nextPane++), paneAssignment.get(`${indicator.id}::${key}`))) : currentPaneIndex);

                        if (!currentSeries.has(seriesId)) {
                            const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                            const priceFormat = (isPaneIndicator || isSlope) ? derivePriceFormatFromPoints(pointsForPrecision, digits) : undefined;
                            const seriesOptions = { ...typeOpts };
                            if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                            if (priceFormat) seriesOptions.priceFormat = priceFormat;
                            if (isSlope) seriesOptions.visible = true;
                            const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                            currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                            if (isPaneIndicator || isSlope) { try { newSeries.getPane().setHeight(100); } catch { /* ignore */ } }
                        } else {
                            const entry = currentSeries.get(seriesId);
                            if (entry.plotType !== type) {
                                const pane = desiredPaneIndex;
                                try {
                                    if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                        chartRef.current.removeSeries(entry.series.posSeries);
                                        chartRef.current.removeSeries(entry.series.negSeries);
                                    } else {
                                        chartRef.current.removeSeries(entry.series);
                                    }
                                } catch { /* ignore */ }
                                const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                                const seriesOptions = { ...typeOpts };
                                if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                                if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(pointsForPrecision, digits);
                                if (isSlope) seriesOptions.visible = true;
                                const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, pane);
                                currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                            } else {
                                const seriesOptions = { ...typeOpts };
                                if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                                if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(padded, digits);
                                if (isSlope) seriesOptions.visible = true;
                                // If pane changed, move series to desired pane
                                const actualPane = entry.pane?.paneIndex?.();
                                if ((isPaneIndicator || isSlope) && typeof actualPane === 'number' && actualPane !== desiredPaneIndex) {
                                    try {
                                        if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                            chartRef.current.removeSeries(entry.series.posSeries);
                                            chartRef.current.removeSeries(entry.series.negSeries);
                                        } else {
                                            chartRef.current.removeSeries(entry.series);
                                        }
                                    } catch { /* ignore */ }
                                    const moved = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                                    currentSeries.set(seriesId, { series: moved, pane: moved.getPane(), plotType: type });
                                } else {
                                    entry.series.applyOptions(seriesOptions);
                                }
                            }
                        }
                        const entryNow = currentSeries.get(seriesId);
                        const hasValues2 = Array.isArray(padded) && padded.some(pt => Number.isFinite(pt?.value));
                        entryNow.series.setData(hasValues2 ? padded : normalized);
                    }
                });
            }
        });

        // Deduplicate labels per pane and apply after series updates
        const unique = [];
        const seen = new Set();
        for (const l of newPaneLabels) {
            const key = `${l.paneIndex}|${l.text}`;
            if (!seen.has(key)) { seen.add(key); unique.push(l); }
        }
    setPaneLabels(unique);
    setOverlayLabels(newOverlayLabels);
    // Measure pane positions shortly after DOM updates
    setTimeout(() => { recomputePaneTops(); }, 0);

    }, [activeIndicators, isBacktest, historicalData, availableIndicators, showVolume, digits, isLoggingEnabled]);

    // Backtest Indicators
    useEffect(() => {
        if (!chartRef.current || !isBacktest) return;

        const chart = chartRef.current;
        const currentBacktestSeries = backtestIndicatorLineRefs.current;
        let colorIndex = 0;
        // Treat any indicator whose base name is 'price' as non-drawable
        const isPriceKey = (key) => {
            const b = key ? key.split('_')[0] : '';
            return String(b).toLowerCase() === 'price';
        };
        // Helper: derive base name (prefix before first underscore)
        const baseNameOf = (key) => {
            if (!key) return '';
            const idx = key.indexOf('_');
            return idx === -1 ? key : key.slice(0, idx);
        };
        // Helper: parse parent from key using multiple heuristics
        // Priority:
        // 1) Explicit alias convention: <child>_src_<parent>
        // 2) Fallback: right-most token that matches any known base indicator name present in dataset (e.g., 'ema_rsi' -> parent 'rsi')
        const parseParentFromKey = (key) => {
            if (!key) return null;
            // Only honor explicit alias convention: <child>_src_<parent>
            const token = '_src_';
            const idx = key.lastIndexOf(token);
            if (idx !== -1) {
                const parent = key.slice(idx + token.length);
                return parent ? parent.split('_')[0] : null;
            }
            // Be conservative: no implicit parent inference to avoid misplacing overlays into pane indicators
            return null;
        };

        // Build base-name index for quick parent lookup
        const allKeys = Object.keys(backtestIndicatorData || {});
        const baseToKeys = new Map();
        const baseNamesSet = new Set();
        for (const k of allKeys) {
            const b = baseNameOf(k);
            if (!baseToKeys.has(b)) baseToKeys.set(b, []);
            baseToKeys.get(b).push(k);
            baseNamesSet.add(String(b).toLowerCase());
        }

        // Volume overlay detection helpers
        const isVolumeOverlayGroup = (key, group) => {
            if (!group || !group.outputs) return false;
            if (group.meta && group.meta.volume_overlay === true) return true;
            // Fallback heuristic: key contains '_source_volume'
            if (/_source_volume/i.test(key)) return true;
            return false;
        };
        const pickPrimaryVolumeMAOutput = (group) => {
            if (!group?.outputs) return null;
            const preferred = ['ema','sma','wma','rma'];
            for (const name of preferred) {
                if (group.outputs[name] && Array.isArray(group.outputs[name]) && group.outputs[name].length > 0) return { name, points: group.outputs[name] };
            }
            return null;
        };

        // Assign panes for any indicator group whose pane_type === 'pane'.
        // Important: even if the indicator is nested (…_src_<parent>), if it declares pane_type 'pane',
        // it should receive its own pane instead of being attached to the parent's (e.g., PRICE) pane.
        const paneAssignment = new Map(); // key -> paneIndex
        // Reserve volume pane index first if volume enabled
        if (showVolume && volumePaneIndexRef.current == null) {
            volumePaneIndexRef.current = 1;
        }
        const reservedVolumePane = showVolume ? volumePaneIndexRef.current : null;
        let nextPaneIndex = (reservedVolumePane != null) ? reservedVolumePane + 1 : 1; // 0 is main price pane
        for (const k of allKeys) {
            const group = backtestIndicatorData[k];
            if (isPriceKey(k)) continue; // skip Price indicator panes entirely
            if (group?.pane_type === 'pane') {
                if (!paneAssignment.has(k)) {
                    paneAssignment.set(k, nextPaneIndex++);
                }
            }
        }

        // Create set of new series ids for diffing
        const newSeriesIds = new Set();
        for (const k of allKeys) {
            const group = backtestIndicatorData[k];
            if (isPriceKey(k)) continue; // skip Price indicator outputs
            if (!group?.outputs) continue;
            for (const outName of Object.keys(group.outputs)) {
                newSeriesIds.add(`${k}-${outName}`);
            }
        }

        // Remove stale series
        currentBacktestSeries.forEach((series, seriesId) => {
            if (!newSeriesIds.has(seriesId)) {
                try {
                    const pane = series.getPane && series.getPane();
                    chart.removeSeries(series);
                    if (pane && typeof pane.getSeries === 'function' && pane.paneIndex && pane.paneIndex() > 0) {
                        if ((pane.getSeries()?.length || 0) === 0) {
                            try { chart.removePane(pane.paneIndex()); } catch { /* ignore */ }
                        }
                    }
                } catch { /* ignore */ }
                currentBacktestSeries.delete(seriesId);
                backtestSeriesKindRef.current.delete(seriesId);
            }
        });

        // Add/update series using parent's pane when nested (…_src_<parent>)
        for (const indicatorKey of allKeys) {
            const indicatorGroup = backtestIndicatorData[indicatorKey];
            if (isPriceKey(indicatorKey)) continue; // don't draw Price indicator in backtest
            if (!indicatorGroup?.outputs) continue;
            // Skip volume overlay groups here; they'll be drawn separately on the volume pane
            if (isVolumeOverlayGroup(indicatorKey, indicatorGroup)) continue;

            const isPaneIndicator = indicatorGroup.pane_type === 'pane';

            // Decide target pane:
            // 1) If this indicator declares pane_type === 'pane' and has an assigned pane, use that
            // 2) Else if nested -> use parent pane (if found), else main (0)
            const parentBase = parseParentFromKey(indicatorKey);
            let targetPane = 0;
            // Prefer own pane when declared as pane-type (even if nested)
            if (isPaneIndicator && paneAssignment.has(indicatorKey)) {
                targetPane = paneAssignment.get(indicatorKey) ?? 0;
            } else if (parentBase) {
                const parentKeys = baseToKeys.get(parentBase) || [];
                const parentKeyWithPane = parentKeys.find(pk => paneAssignment.has(pk));
                if (parentKeyWithPane) targetPane = paneAssignment.get(parentKeyWithPane) ?? 0;
            } else if (isPaneIndicator) {
                // Base indicator with its own pane
                targetPane = paneAssignment.get(indicatorKey) ?? 0;
            }

            for (const outputName of Object.keys(indicatorGroup.outputs)) {
                const rawPoints = indicatorGroup.outputs[outputName];
                const seriesId = `${indicatorKey}-${outputName}`;
                if (!rawPoints) continue;

                // Normalize data — keep null/NaN values as whitespace data
                // so lightweight-charts renders proper gaps instead of diagonals
                const formatted = rawPoints
                    .filter(p => p && typeof p.time === 'number')
                    .map(p => {
                        let t = p.time;
                        if (t > 1000000000000) t = Math.floor(t / 1000);
                        const pt = { time: t };
                        if (Number.isFinite(p.value)) pt.value = p.value;
                        if (typeof p.color === 'string') pt.color = p.color;
                        return pt;
                    })
                    .sort((a, b) => a.time - b.time);

                const dedupedData = deduplicateData(formatted);
                if (dedupedData.length === 0) continue;

                // Slope outputs go to their own dedicated panes (like live)
                const slopeLike = isSlopeOutputKey(outputName);
                let finalPane = targetPane;
                if (slopeLike) {
                    const key = `${indicatorKey}::${outputName}`;
                    if (!paneAssignment.has(key)) {
                        paneAssignment.set(key, nextPaneIndex++);
                    }
                    finalPane = paneAssignment.get(key) ?? targetPane;
                }

                // Choose series type per output. Common case: MACD 'histogram' should be bars, others lines.
                const outputIsHistogram = /hist/i.test(outputName)
                    || (indicatorGroup?.visual_schema && indicatorGroup.visual_schema.series && indicatorGroup.visual_schema.series[outputName]?.plotType === 'histogram');
                const chosenSeriesType = slopeLike ? LineSeries : (outputIsHistogram ? HistogramSeries : LineSeries);
                const seriesOptions = {
                    color: indicatorColors[colorIndex % indicatorColors.length],
                    lineWidth: 1,
                };
                // Overlays on main price pane: hide current-value price line
                if (!(isPaneIndicator || finalPane > 0)) {
                    seriesOptions.priceLineVisible = false;
                }
                // For pane series (slope or pane-type), set precision from data
                if (slopeLike || isPaneIndicator || finalPane > 0) {
                    seriesOptions.priceFormat = derivePriceFormatFromPoints(dedupedData, digits);
                }

                if (!currentBacktestSeries.has(seriesId)) {
                    const newSeries = chart.addSeries(chosenSeriesType, seriesOptions, finalPane);
                    if ((slopeLike || isPaneIndicator || finalPane > 0) && chart && typeof newSeries.getPane === 'function') {
                        try { newSeries.getPane().setHeight(100); } catch { /* ignore */ }
                    }
                    currentBacktestSeries.set(seriesId, newSeries);
                    backtestSeriesKindRef.current.set(seriesId, outputIsHistogram ? 'histogram' : 'line');
                    try { newSeries.setData(dedupedData); } catch { /* ignore */ }
                } else {
                    const existing = currentBacktestSeries.get(seriesId);
                    const existingPane = existing?.getPane && existing.getPane();
                    const existingIdx = existingPane && typeof existingPane.paneIndex === 'function' ? existingPane.paneIndex() : 0;
                    const existingKind = backtestSeriesKindRef.current.get(seriesId);
                    // If pane or series type changed, recreate
                    if (existingIdx !== finalPane || existingKind !== (outputIsHistogram ? 'histogram' : 'line')) {
                        try { chart.removeSeries(existing); } catch { /* ignore */ }
                        const newSeries = chart.addSeries(chosenSeriesType, seriesOptions, finalPane);
                        if ((slopeLike || isPaneIndicator || finalPane > 0) && chart && typeof newSeries.getPane === 'function') {
                            try { newSeries.getPane().setHeight(100); } catch { /* ignore */ }
                        }
                        currentBacktestSeries.set(seriesId, newSeries);
                        backtestSeriesKindRef.current.set(seriesId, outputIsHistogram ? 'histogram' : 'line');
                        try { newSeries.setData(dedupedData); } catch { /* ignore */ }
                    } else {
                        try { existing.applyOptions(seriesOptions); } catch { /* ignore */ }
                        try { existing.setData(dedupedData); } catch { /* ignore */ }
                    }
                }
                colorIndex++;
            }
        }

        // Handle volume overlay MAs (after other series so pane indices stable)
        if (isBacktest && showVolume) {
            // Ensure volume pane & series exist to attach
            ensureVolumeSeries();
            const volPaneIndex = volumePaneIndexRef.current;
            const createdIds = new Set();
            const overlayLabels = [];
            allKeys.forEach(key => {
                const group = backtestIndicatorData[key];
                if (!isVolumeOverlayGroup(key, group)) return;
                const primary = pickPrimaryVolumeMAOutput(group);
                if (!primary) return; // nothing to draw
                const seriesId = `__volma__${key}__${primary.name}`;
                // If this group/output was previously added to main panes by generic logic (older render), remove it
                const legacyId = `${key}-${primary.name}`;
                if (currentBacktestSeries.has(legacyId)) {
                    try { chart.removeSeries(currentBacktestSeries.get(legacyId)); } catch { /* ignore */ }
                    currentBacktestSeries.delete(legacyId);
                }
                createdIds.add(seriesId);
                // Format data
                const formatted = primary.points
                    .filter(p => p && typeof p.time === 'number' && Number.isFinite(p.value))
                    .map(p => ({ time: p.time > 1000000000000 ? Math.floor(p.time/1000) : p.time, value: p.value }))
                    .sort((a,b)=>a.time-b.time);
                if (formatted.length === 0) return;
                if (!volumeOverlaySeriesRef.current.has(seriesId)) {
                    const colorIdx = volumeOverlaySeriesRef.current.size % indicatorColors.length;
                    const s = chart.addSeries(LineSeries, {
                        color: indicatorColors[colorIdx],
                        lineWidth: 2,
                        priceScaleId: 'volume',
                        priceLineVisible: false,
                    }, volPaneIndex ?? 0);
                    volumeOverlaySeriesRef.current.set(seriesId, s);
                }
                const sref = volumeOverlaySeriesRef.current.get(seriesId);
                try { sref.setData(deduplicateData(formatted)); } catch { /* ignore */ }
                // Build label text (attempt to parse length from key tokens)
                const tokens = key.split('_');
                let lengthToken = tokens[tokens.indexOf('length')+1];
                if (!Number.isFinite(Number(lengthToken))) lengthToken = undefined;
                const base = tokens[0] ? tokens[0].toUpperCase() : primary.name.toUpperCase();
                const label = `${base}${lengthToken ? `(${lengthToken})` : ''} Vol`;
                overlayLabels.push({ id: seriesId, text: label, color: sref.options().color });
            });
            // Remove stale volume overlay series
            volumeOverlaySeriesRef.current.forEach((series, id) => {
                if (!createdIds.has(id)) {
                    try { chart.removeSeries(series); } catch { /* ignore */ }
                    volumeOverlaySeriesRef.current.delete(id);
                }
            });
            setBacktestVolumeOverlayLabels(overlayLabels);
        } else {
            // Clear overlays when volume hidden
            if (volumeOverlaySeriesRef.current.size) {
                volumeOverlaySeriesRef.current.forEach((s) => { try { chart.removeSeries(s); } catch { /* ignore */ } });
                volumeOverlaySeriesRef.current.clear();
                setBacktestVolumeOverlayLabels([]);
            }
        }
    }, [backtestIndicatorData, isBacktest, showVolume, ensureVolumeSeries]);


    // Indicator actions (only for live trading)
    const handleAddIndicator = (indicator, params, paneType, scaleType) => {
        if (isBacktest) return; // Prevent adding indicators in backtest mode
        const key = getIndicatorKey(indicator, params, selectedSymbol, selectedTimeframe, paneType);
        sendIndicatorAction('add_indicator', { 
            indicator, 
            params, 
            unique_id: key, 
            count: 2700, // Or a suitable number
            pane_type: paneType, 
            scale_type: scaleType 
        });
    };

    const handleRemoveIndicator = (indicatorId) => {
        if (isBacktest) return; // Prevent removing indicators in backtest mode
        const indicatorToRemove = activeIndicators.find(ind => ind.id === indicatorId);
        if (indicatorToRemove && indicatorToRemove.backendUniqueId) {
            sendIndicatorAction('remove_indicator', { unique_id: indicatorToRemove.backendUniqueId });
        } else {
            console.error("Could not find indicator to remove or it's missing a backendUniqueId:", indicatorId);
        }
    };

    // Indicator meta helpers
    const findMetaByName = (name) => {
        if (!name) return null;
        const lower = String(name).toLowerCase();
        return (availableIndicators && availableIndicators[lower]) || null;
    };
    // Helper to resolve a canonical id for an indicator name (fallback lower-cased name)
    const getIdByName = (name) => {
        const meta = findMetaByName(name);
        return meta?.id || meta?.name || (name ? String(name) : '').toLowerCase();
    };

    const handleSaveProfile = async (profileName) => {
        // Build IndicatorSpec list (backend-expected shape)
        const indicatorsPayload = [];
        // 1) Base indicators: name + PRICE input
        activeIndicators.forEach((i) => {
            if (!i?.name) return;
            indicatorsPayload.push({
                name: i.name,
                params: i.params || {},
                inputs: [ { ref: 'PRICE', params: { field: 'close' } } ],
            });
        });
        // 2) Nested spec overlays: child with input = parent spec
        try {
            if (specStreams && typeof specStreams === 'object') {
                Object.entries(specStreams).forEach(([uid, entry]) => {
                    if (!uid || (!entry?.attachToId && !entry?.attachToMeta)) return;
                    // Try to derive names/params from UID; fall back to attach meta
                    let childName, childParams = {}, parentName, parentParams = {}, output;
                    try {
                        const parts = String(uid).split(':');
                        if (parts.length >= 7 && parts[0] === 'spec' && parts[2] === 'on') {
                            childName = parts[1];
                            const parentId = parts[3];
                            output = parts[4] && parts[4] !== 'value' ? parts[4] : undefined;
                            const tail = parts.slice(5).join(':');
                            const pivot = tail.lastIndexOf('}:');
                            if (pivot !== -1) {
                                const a = tail.slice(0, pivot + 1);
                                const b = tail.slice(pivot + 2);
                                childParams = JSON.parse(a);
                                parentParams = JSON.parse(b);
                            }
                            // Prefer human name if we have it
                            parentName = entry?.attachToMeta?.parentName || parentId;
                        }
                    } catch { /* ignore */ }
                    if (!parentName) parentName = entry?.attachToMeta?.parentName;
                    if (!parentParams) parentParams = entry?.attachToMeta?.parentParams || {};
                    if (!childName) childName = undefined; // unknown child; skip if missing
                    if (!parentName || !childName) return;

                    // Use provided names directly (backend does not validate here)
                    const parentNode = {
                        name: parentName,
                        params: parentParams || {},
                        inputs: [ { ref: 'PRICE', params: { field: 'close' } } ],
                        ...(output ? { output } : {}),
                    };
                    indicatorsPayload.push({
                        name: childName,
                        params: childParams || {},
                        inputs: [ parentNode ],
                    });
                });
            }
        } catch (e) {
            console.warn('Failed to serialize nested specs for profile:', e);
        }
        try {
            const newProfile = await createChartProfile(
                profileName,
                selectedSymbol,
                selectedTimeframe,
                indicatorsPayload
            );
            setChartProfiles((prev) => [...normalizeChartProfiles(prev), newProfile]);
            setSelectedProfile(newProfile.id);
            alert('Profile saved!');
        } catch (error) {
            console.error('Failed to save profile:', error);
            alert('Failed to save profile.');
        }
    };

    const handleLoadProfile = (profileId) => {
        const profile = normalizeChartProfiles(chartProfiles).find(p => p.id === profileId);
        if (!profile) return;

        // Clear existing indicators before loading new ones
        activeIndicators.forEach(ind => {
            if (ind.backendUniqueId) {
                handleRemoveIndicator(ind.id);
            }
        });

        // Helpers to add base indicators and restore nested specs
        const addedBaseKeys = new Set();
        const ensureBaseFromNode = (node) => {
            if (!node || !node.name) return;
            const meta = findMetaByName(node.name);
            const pane = meta?.pane_type || 'overlay';
            const scale = meta?.scale_type;
            const key = getIndicatorKey(node.name, node.params || {}, selectedSymbol, selectedTimeframe, pane);
            if (addedBaseKeys.has(key)) return;
            handleAddIndicator(node.name, node.params || {}, pane, scale);
            addedBaseKeys.add(key);
        };
        const recurseEnsureChain = (node) => {
            if (!node || !node.inputs || !Array.isArray(node.inputs) || node.inputs.length === 0) return;
            const first = node.inputs[0];
            if (first.ref === 'PRICE') return;
            if (first.name) {
                recurseEnsureChain(first);
                ensureBaseFromNode(first);
            }
        };

        const arr = Array.isArray(profile.indicators) ? profile.indicators : [];
        arr.forEach((spec) => {
            if (!spec || typeof spec !== 'object') return;
            const hasNested = Array.isArray(spec.inputs) && spec.inputs.length > 0 && !!spec.inputs[0] && !spec.inputs[0].ref;
            if (!hasNested) {
                // Base indicator: name + PRICE input
                ensureBaseFromNode(spec);
                return;
            }
            // Nested: ensure parent chain exists, then subscribe overlay attached to direct parent
            const parent = spec.inputs[0];
            recurseEnsureChain(spec); // ensure all upstream parents are added
            ensureBaseFromNode(parent);
            const parentMeta = findMetaByName(parent.name);
            const pane = parentMeta?.pane_type || 'overlay';
            const attachToId = getIndicatorKey(parent.name, parent.params || {}, selectedSymbol, selectedTimeframe, pane);
            // Build spec for subscribeSpec exactly as saved
            const parentNode = { ...parent };
            // Normalize leaf PRICE input if missing params
            try {
                if (Array.isArray(parentNode.inputs) && parentNode.inputs[0]?.ref === 'PRICE' && !parentNode.inputs[0].params) {
                    parentNode.inputs[0] = { ref: 'PRICE', params: { field: 'close' } };
                }
            } catch { /* ignore */ }
            const childName = spec.name;
            const childParams = spec.params || {};
            const childId = getIdByName(childName);
            const parentId = getIdByName(parent.name);
            const out = parent.output;
            const uid = `spec:${childId}:on:${parentId}:${out || 'value'}:${JSON.stringify(childParams)}:${JSON.stringify(parent.params || {})}`;
            const overlaySpec = { name: childId, params: childParams, inputs: [ parentNode ] };
            const attachToMeta = { parentId: attachToId, parentName: parent.name, parentParams: parent.params || {} };
            subscribeSpec && subscribeSpec(uid, overlaySpec, { timeframe: selectedTimeframe, count: 2500, attachToId, attachToMeta });
        });
        setSelectedProfile(profileId);
    };

    const handleDeleteProfile = async (profileId) => {
        try {
            await deleteChartProfile(profileId);
            setChartProfiles((prev) => normalizeChartProfiles(prev).filter(p => p.id !== profileId));
            if (selectedProfile === profileId) {
                setSelectedProfile('');
            }
            alert('Profile deleted.');
        } catch (error) {
            console.error('Failed to delete profile:', error);
            alert('Failed to delete profile.');
        }
    };

    // Helper to immediately apply visuals for one indicator instance
    const applyVisualsForIndicator = (indicator) => {
        if (!chartRef.current || !indicator) return;
        const currentSeries = indicatorSeriesRef.current;
        const isPaneIndicator = indicator.paneType === 'pane';

        const indicatorMeta = getIndicatorMeta(availableIndicators, indicator?.name);
        const visualDefaults = indicatorMeta?.visual_defaults || {};
        const overrides = indicator?.visual_overrides || {};
        const effectiveVisuals = deepMerge(visualDefaults, overrides);
        const visualsByKey = effectiveVisuals?.series || {};
        const outKeys = getOutputKeys(indicator, indicatorMeta);

        if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
            try {
                const sample = Array.isArray(indicator.data) ? indicator.data.slice(0, 3) : indicator.data;
                console.log('[DMI][apply] outKeys:', outKeys, 'sample:', sample);
            } catch { /* ignore */ }
        }

        // Multi-output provided as array-of-objects with multiple keys
        if (Array.isArray(indicator.data) && outKeys.length > 1) {
            outKeys.forEach((key) => {
                const seriesId = `${indicator.id}-${key}`;
                const normalized = normalizeSeriesPoints(indicator.data, key);
                const padded = padIndicatorData(historicalData, normalized);

                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                const vForKey = picked.visual;
                const fallbacks = {
                    color: indicator.params?.[`${key}_color`] || indicator.params?.color || '#FFD700',
                    lineWidth: indicator.params?.[`${key}_width`] || indicator.params?.width || 2,
                    lineStyle: indicator.params?.[`${key}_style`] || indicator.params?.style || LineStyle.Solid || 0,
                };
                const options = buildOptionsFromVisuals(vForKey, fallbacks);
                const { SeriesCtor, type } = resolveSeriesType(vForKey?.plotType || indicator.params?.[`${key}_plotType`] || indicator.params?.plotType || 'line');
                const typeOpts = toSeriesTypeOptions(type, options);

                const entry = currentSeries.get(seriesId);
                if (!entry) return;
                if (entry.plotType !== type) {
                    const pane = entry.pane?.paneIndex?.() ?? (isPaneIndicator ? 1 : 0);
                    chartRef.current.removeSeries(entry.series);
                    const newSeries = chartRef.current.addSeries(SeriesCtor, {
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                        title: `${indicator.name} ${key}`,
                    }, pane);
                    currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    newSeries.setData(padded);
                } else {
                    entry.series.applyOptions({
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                    });
                    entry.series.setData(padded);
                }
            });
            return;
        }

        // Single value
        if (Array.isArray(indicator.data) && indicator.data.length > 0) {
            const valueKey = outKeys[0] || (indicator.data_keys && indicator.data_keys.length > 0 ? indicator.data_keys[0] : 'value');
            const normalized = normalizeSeriesPoints(indicator.data, valueKey);
            const padded = padIndicatorData(historicalData, normalized);

            const picked = pickSeriesVisual(visualsByKey, indicatorMeta, valueKey);
            const vForKey = picked.visual;
            const fallbacks = {
                color: indicator.params?.color || '#FFD700',
                lineWidth: indicator.params?.width || 2,
                lineStyle: indicator.params?.style || LineStyle.Solid || 0,
            };
            const options = buildOptionsFromVisuals(vForKey, fallbacks);
            const reqPlot = (vForKey?.plotType || indicator.params?.plotType || 'line');
            // If this is a pane indicator with a single line, switch to baseline to color negatives red
            const useBaseline = (indicator.paneType === 'pane') && (outKeys.length === 1) && (String(reqPlot).toLowerCase() === 'line');
            const chosenPlot = useBaseline ? 'baseline' : reqPlot;
            const { SeriesCtor, type } = resolveSeriesType(chosenPlot);
            const typeOpts = toSeriesTypeOptions(type, { ...options, negativeColor: NEGATIVE_LINE_COLOR });

            const entry = currentSeries.get(indicator.id);
            if (!entry) return; // nothing to update yet
            if (entry.plotType !== type) {
                const pane = entry.pane?.paneIndex?.() ?? (isPaneIndicator ? 1 : 0);
                chartRef.current.removeSeries(entry.series);
                const newSeries = chartRef.current.addSeries(SeriesCtor, {
                    ...typeOpts,
                    ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                    title: indicator.name,
                }, pane);
                currentSeries.set(indicator.id, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                newSeries.setData(padded);
            } else {
                entry.series.applyOptions({
                    ...typeOpts,
                    ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                });
                entry.series.setData(padded);
            }
            return;
        }

        // Multi-value object-of-arrays
        if (typeof indicator.data === 'object' && indicator.data) {
            Object.keys(indicator.data).forEach((key) => {
                const seriesId = `${indicator.id}-${key}`;
                const seriesData = indicator.data[key];
                if (!seriesData || seriesData.length === 0) return;

                const normalized = normalizeSeriesPoints(seriesData, key);
                if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
                    try { console.log('[DMI][apply][norm]', key, normalized.slice(0, 3)); } catch { /* ignore */ }
                }
                const padded = padIndicatorData(historicalData, normalized);
                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                const vForKey = picked.visual;
                const fallbacks = {
                    color: indicator.params?.[`${key}_color`] || '#FFD700',
                    lineWidth: indicator.params?.[`${key}_width`] || 2,
                    lineStyle: indicator.params?.[`${key}_style`] || LineStyle.Solid || 0,
                };
                const options = buildOptionsFromVisuals(vForKey, fallbacks);
                const reqPlot = (vForKey?.plotType || indicator.params?.[`${key}_plotType`] || 'line');
                // If this pane indicator only has one series key total, use baseline for negative coloring
                const keysAll = Object.keys(indicator.data || {});
                const useBaseline = (indicator.paneType === 'pane') && (keysAll.length === 1) && (String(reqPlot).toLowerCase() === 'line');
                const chosenPlot = useBaseline ? 'baseline' : reqPlot;
                const { SeriesCtor, type } = resolveSeriesType(chosenPlot);
                const typeOpts = toSeriesTypeOptions(type, { ...options, negativeColor: NEGATIVE_LINE_COLOR });

                const entry = currentSeries.get(seriesId);
                if (!entry) return;
                if (entry.plotType !== type) {
                    const pane = entry.pane?.paneIndex?.() ?? (isPaneIndicator ? 1 : 0);
                    chartRef.current.removeSeries(entry.series);
                    const newSeries = chartRef.current.addSeries(SeriesCtor, {
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                        title: `${indicator.name} ${key}`,
                    }, pane);
                    currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    newSeries.setData(padded);
                } else {
                    entry.series.applyOptions({
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                    });
                    entry.series.setData(padded);
                }
            });
        }
    };

    // Load overrides cache from localStorage once
    useEffect(() => {
        try {
            const raw = localStorage.getItem(OVERRIDES_STORAGE_KEY);
            if (raw) {
                const obj = JSON.parse(raw);
                const map = new Map(Object.entries(obj));
                visualOverridesCacheRef.current = map;
            }
    } catch (error) {
            console.error('Failed to load visual overrides cache', error);
        }
    }, []);

    function persistOverridesCache() {
        try {
            const obj = Object.fromEntries(visualOverridesCacheRef.current);
            localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(obj));
    } catch (error) {
            console.error('Failed to persist visual overrides cache', error);
        }
    }

    // Hydrate missing visual_overrides from cache when indicators (re)appear
    useEffect(() => {
        if (isBacktest) return;
        if (!Array.isArray(activeIndicators) || activeIndicators.length === 0) return;
        const cache = visualOverridesCacheRef.current;
        activeIndicators.forEach((ind) => {
            if (!ind) return;
            const hasOverrides = ind.visual_overrides && Object.keys(ind.visual_overrides || {}).length > 0;
            if (hasOverrides) return;
            const sig = getIndicatorSignature(ind);
            const cached = cache.get(sig);
            if (cached) {
                // Apply and set into state (which also triggers live chart update)
                handleUpdateIndicatorVisuals(ind.id, cached);
            }
        });
    }, [activeIndicators, isBacktest]);

    const handleUpdateIndicatorVisuals = (indicatorId, visualOverrides) => {
        // Build a new array value (avoid functional update to ensure context compatibility)
        const newActive = Array.isArray(activeIndicators)
            ? activeIndicators.map((ind) => (ind.id === indicatorId ? { ...ind, visual_overrides: visualOverrides || {} } : ind))
            : activeIndicators;
        setActiveIndicators(newActive);

        // Update cache using the indicator signature
        const target = Array.isArray(newActive) ? newActive.find((i) => i.id === indicatorId) : null;
        if (target) {
            const sig = getIndicatorSignature(target);
            visualOverridesCacheRef.current.set(sig, visualOverrides || {});
            persistOverridesCache();
        }

        // Also apply immediately to the existing series if present
        const updated = Array.isArray(newActive) ? newActive.find((i) => i.id === indicatorId) : null;
        if (updated) {
            // Give React a tick to commit state before applying options
            setTimeout(() => applyVisualsForIndicator(updated), 0);
        }
    };

    // Update indicator parameters by removing and re-adding the indicator (live trading only)
    const handleUpdateIndicatorParams = (indicatorId, nextParams) => {
        if (isBacktest) return; // No param updates in backtest mode
        const target = Array.isArray(activeIndicators) ? activeIndicators.find(i => i.id === indicatorId) : null;
        if (!target) return;
        const name = target.name;
        const paneType = target.paneType;
        const scaleType = target.scaleType;

        // Preserve any cached visual overrides by copying to the new signature as well
        try {
            const oldSig = getIndicatorSignature(target);
            const cached = visualOverridesCacheRef.current.get(oldSig);
            if (cached) {
                const fakeNext = { ...target, params: nextParams };
                const newSig = getIndicatorSignature(fakeNext);
                visualOverridesCacheRef.current.set(newSig, cached);
                persistOverridesCache();
            }
        } catch { /* ignore */ }

        // Remove existing instance (if we know its backend id)
        if (target.backendUniqueId) {
            try { sendIndicatorAction('remove_indicator', { unique_id: target.backendUniqueId }); } catch { /* ignore */ }
        }

        // Add a new instance with updated params and a fresh unique id
        const newKey = getIndicatorKey(name, nextParams || {}, selectedSymbol, selectedTimeframe, paneType);
        try {
            sendIndicatorAction('add_indicator', {
                indicator: name,
                params: nextParams || {},
                unique_id: newKey,
                count: 2700,
                pane_type: paneType,
                scale_type: scaleType,
            });
        } catch (e) {
            console.error('Failed to send update (remove+add) for indicator params:', e);
        }
    };

    // Expose stable bridges so pane chip portals can call back into this component
    useEffect(() => {
        window.__isOverlayVisible = (id) => isOverlayVisible(id);
        window.__toggleOverlayVisibilityById = (id) => toggleOverlayVisibilityById(id);
        window.__openOverlayVisualEditor = (id) => openOverlayVisualEditor(id);
        window.__handleRemoveIndicator = (id) => handleRemoveIndicator(id);
    });

    // Observe pane DOM changes to keep chips attached on resize/add/remove
    useEffect(() => {
        const el = chartContainerRef.current;
        if (!el || typeof MutationObserver === 'undefined') return;
        const obs = new MutationObserver(() => {
            try {
                attachPaneChips(
                    paneLabels,
                    el,
                    (id) => isOverlayVisible(id),
                    (id) => toggleOverlayVisibilityById(id),
                    (id) => openOverlayVisualEditor(id),
                    (id) => handleRemoveIndicator(id)
                );
            } catch { /* ignore */ }
        });
        obs.observe(el, { childList: true, subtree: true, attributes: false });
    return () => { try { obs.disconnect(); } catch { /* ignore */ } };
    }, [paneLabels]);

    // Overlay label actions
    const isOverlayVisible = (indicatorId) => {
        const target = Array.isArray(activeIndicators) ? activeIndicators.find(i => i.id === indicatorId) : null;
        if (!target) return true;
        const eff = getEffectiveVisuals(target, availableIndicators);
        const keys = Object.keys(eff.series || {})
            .concat(getOutputKeys(target, getIndicatorMeta(availableIndicators, target?.name)))
            .filter((v, i, a) => v && a.indexOf(v) === i);
        if (keys.length === 0) return true;
        // Visible if any key not explicitly false
        return keys.some(k => eff.series?.[k]?.visible !== false);
    };

    const toggleOverlayVisibilityById = (indicatorId) => {
        const target = Array.isArray(activeIndicators) ? activeIndicators.find(i => i.id === indicatorId) : null;
        if (!target) return;
        const eff = getEffectiveVisuals(target, availableIndicators);
        const keys = Object.keys(eff.series || {})
            .concat(getOutputKeys(target, getIndicatorMeta(availableIndicators, target?.name)))
            .filter((v, i, a) => v && a.indexOf(v) === i);
        const anyVisible = keys.length === 0 ? true : keys.some(k => eff.series?.[k]?.visible !== false);
        const nextVisible = !anyVisible;
        const draft = { series: {} };
        (keys.length ? keys : ['default']).forEach((k) => {
            draft.series[k] = { ...(eff.series?.[k] || {}), visible: nextVisible };
        });
        handleUpdateIndicatorVisuals(indicatorId, buildOverridesAgainstDefaults(target, draft));
    };

    // Build overrides against defaults for a single indicator
    const buildOverridesAgainstDefaults = (indicator, draft) => {
        const meta = getIndicatorMeta(availableIndicators, indicator?.name);
        const defaults = meta?.visual_defaults || {};
        const overrides = { series: {} };
        const draftSeries = draft.series || {};
        const defaultSeries = defaults.series || {};
        Object.keys(draftSeries).forEach((k) => {
            const d = draftSeries[k] || {};
            const def = defaultSeries[k] || {};
            const diff = {};
            Object.keys(d).forEach((f) => {
                if (d[f] !== def[f] && d[f] !== undefined) diff[f] = d[f];
            });
            if (Object.keys(diff).length) overrides.series[k] = diff;
        });
        return overrides;
    };

    // Open the same visual editor used in IndicatorControls via a custom event
    const openOverlayVisualEditor = (indicatorId) => {
        try {
            const evt = new CustomEvent('open-indicator-visual-editor', { detail: { indicatorId } });
            window.dispatchEvent(evt);
    } catch { /* ignore */ }
    };

    // Clear all indicator series on instrument/context change to avoid duplicates
    useEffect(() => {
        if (isBacktest) return;
        const chart = chartRef.current;
        const currentSeries = indicatorSeriesRef.current;
        if (chart && currentSeries.size > 0) {
            currentSeries.forEach(({ series }) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            currentSeries.clear();
        }
        // Clear active indicators; backend will repopulate for new context
        setActiveIndicators([]);
        // Also immediately clear the main price series so no old bars remain visible
        if (seriesRef.current) {
            try { seriesRef.current.setData([]); } catch { /* ignore */ }
        }
    // Remove volume pane
    removeVolumeSeries();
        barsRef.current = [];
        timeToIndexRef.current = new Map();
    }, [selectedSymbol, selectedTimeframe, selectedAccount, isBacktest]);

    // Clear spec overlay series on instrument/context change
    useEffect(() => {
        if (isBacktest) return;
        const chart = chartRef.current;
        if (chart && specSeriesRef.current.size > 0) {
            specSeriesRef.current.forEach((series) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            specSeriesRef.current.clear();
        }
    }, [selectedSymbol, selectedTimeframe, selectedAccount, isBacktest]);

    // Deterministic color per nested spec series key
    const colorForKey = useCallback((key) => {
        const s = String(key || '');
        let h = 0;
        for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
        const idx = Math.abs(h) % indicatorColors.length;
        return indicatorColors[idx] || '#FFD700';
    }, []);

    // Ensure a nested spec overlay series exists
    const ensureSpecSeries = useCallback((seriesKey, hostPane) => {
        const chart = chartRef.current;
        if (!chart) return null;
        const existing = specSeriesRef.current.get(seriesKey);
        if (existing) return existing;
        try {
            // Prefer adding to the same pane as the host indicator if provided
            const options = { color: colorForKey(seriesKey), lineWidth: 2 };
            // If rendering on the main pane (overlay), hide dotted current-value line
            if (hostPane === 0 || hostPane === undefined || hostPane === null) {
                options.priceLineVisible = false;
            }
            const s = chart.addSeries(LineSeries, options, (hostPane !== undefined && hostPane !== null) ? hostPane : undefined);
            specSeriesRef.current.set(seriesKey, s);
            return s;
        } catch {
            return null;
        }
    }, [colorForKey]);

    // Drive spec overlay series from specStreams (seed and updates are reflected via state changes)
    useEffect(() => {
        if (isBacktest) return;
        const chart = chartRef.current;
        if (!chart || !specStreams || typeof specStreams !== 'object') return;
        // Helper: sort ascending by time and remove duplicate/equal times (keep last)
        const sortAscUnique = (pts) => {
            if (!Array.isArray(pts)) return [];
            const a = pts.slice().sort((x, y) => (x.time - y.time));
            const out = [];
            let lastTime = -Infinity;
            for (const pt of a) {
                if (typeof pt?.time !== 'number' || !Number.isFinite(pt.time)) continue;
                if (pt.time > lastTime) {
                    out.push(pt);
                    lastTime = pt.time;
                } else if (pt.time === lastTime && out.length > 0) {
                    // replace previous at same time with the latest
                    out[out.length - 1] = pt;
                }
            }
            return out;
        };
        const expected = new Set();
        Object.entries(specStreams).forEach(([uid, entry]) => {
            if (!entry || entry.data == null) return;
            // If this entry lacks both attachToId and attachToMeta, treat as orphan and skip
            if (!entry.attachToId && !entry.attachToMeta) {
                if (isLoggingEnabled) console.warn('[SpecOverlay] skip orphan spec stream', { uid });
                return;
            }
            // If this stream is attached to a specific indicator pane, try to reuse that pane (by index)
            let targetPaneIndex = undefined;
            const resolveHostByMeta = () => {
                const meta = entry.attachToMeta;
                if (!meta) return undefined;
                // Try id first
                let host = undefined;
                if (meta.parentId) {
                    host = indicatorSeriesRef.current.get(meta.parentId);
                    if (!host) {
                        for (const [k, v] of indicatorSeriesRef.current.entries()) {
                            if (typeof k === 'string' && k.startsWith(meta.parentId + '-')) { host = v; break; }
                        }
                    }
                }
                // Fallback by matching current activeIndicators by name/params
                if (!host && meta.parentName) {
                    const candidates = activeIndicators.filter(i => String(i.name).toLowerCase() === String(meta.parentName).toLowerCase());
                    let chosen = candidates[0];
                    if (meta.parentParams && candidates.length > 1) {
                        const want = JSON.stringify(meta.parentParams);
                        chosen = candidates.find(c => JSON.stringify(c.params||{}) === want) || candidates[0];
                    }
                    if (chosen) {
                        // Use indicatorSeriesRef to get a concrete series entry for this indicator id
                        host = indicatorSeriesRef.current.get(chosen.id);
                        if (!host) {
                            for (const [k, v] of indicatorSeriesRef.current.entries()) {
                                if (typeof k === 'string' && k.startsWith(chosen.id + '-')) { host = v; break; }
                            }
                        }
                    }
                }
                if (isLoggingEnabled) console.log('[SpecOverlay] resolveHostByMeta', { meta, found: !!host });
                return host;
            };
            if (entry.attachToId || entry.attachToMeta) {
                let host = indicatorSeriesRef.current.get(entry.attachToId);
                if (!host) {
                    // Fallback: multi-output indicators are stored as `${id}-${key}`; pick any
                    for (const [k, v] of indicatorSeriesRef.current.entries()) {
                        if (typeof k === 'string' && k.startsWith(entry.attachToId + '-')) { host = v; break; }
                    }
                }
                if (!host) host = resolveHostByMeta();
                if (host && host.pane && typeof host.pane.paneIndex === 'function') {
                    const idx = host.pane.paneIndex();
                    if (typeof idx === 'number') targetPaneIndex = idx;
                }
                if (isLoggingEnabled) console.log('[SpecOverlay] host pane', { attachToId: entry.attachToId, attachToMeta: entry.attachToMeta, targetPaneIndex });
            }
            const keys = Array.isArray(entry.data_keys) && entry.data_keys.length > 0 ? entry.data_keys : ['value'];
            const valueForPoint = (p, k) => {
                if (!p) return undefined;
                if (Number.isFinite(p.value)) return p.value;
                if (k && Number.isFinite(p[k])) return p[k];
                if (p.value && typeof p.value === 'object' && Number.isFinite(p.value[k])) return p.value[k];
                if (p.outputs && typeof p.outputs === 'object' && Number.isFinite(p.outputs[k])) return p.outputs[k];
                // Fallback: first numeric field other than time
                for (const kk of Object.keys(p)) {
                    if (kk === 'time') continue;
                    const v = p[kk];
                    if (Number.isFinite(v)) return v;
                }
                return undefined;
            };
            if (Array.isArray(entry.data)) {
                const key = `${uid}:value`;
                expected.add(key);
                // If attached to a parent but no pane yet, defer until pane resolves to avoid landing on wrong pane
                if ((entry.attachToId || entry.attachToMeta) && typeof targetPaneIndex !== 'number') {
                    if (isLoggingEnabled) console.log('[SpecOverlay] defer create (no pane yet)', { key, attachToId: entry.attachToId, attachToMeta: entry.attachToMeta });
                    return;
                }
                let s = ensureSpecSeries(key, targetPaneIndex);
                // If we created without targetPane earlier, move to target pane when it appears by recreating
                if (s && typeof targetPaneIndex === 'number') {
                    try {
                        const currentPane = s.getPane && s.getPane();
                        const currentIdx = currentPane && currentPane.paneIndex ? currentPane.paneIndex() : undefined;
                        if (typeof currentIdx === 'number' && currentIdx !== targetPaneIndex) {
                            if (isLoggingEnabled) console.log('[SpecOverlay] relocating series', { key, from: currentIdx, to: targetPaneIndex });
                            chart.removeSeries(s);
                            specSeriesRef.current.delete(key);
                            s = ensureSpecSeries(key, targetPaneIndex);
                        }
                    } catch { /* ignore */ }
                }
                if (s) {
                    const primary = keys[0];
                    const filtered = entry.data
                        .map(p => ({ p, v: valueForPoint(p, primary) }))
                        .filter(({ p, v }) => typeof p?.time === 'number' && Number.isFinite(p.time) && Number.isFinite(v));
                    if (isLoggingEnabled && filtered.length === 0 && Array.isArray(entry.data) && entry.data.length > 0) {
                        const sample = entry.data[entry.data.length - 1];
                        console.warn('[SpecOverlay] No numeric points found for single-series', { key, primary, sampleKeys: Object.keys(sample || {}) });
                    }
                    if (isLoggingEnabled) console.log('[SpecOverlay] setData', { key, points: filtered.length, attachToId: entry.attachToId, primary });
                    const points = filtered.map(({ p, v }) => ({ time: p.time > 1000000000000 ? Math.floor(p.time/1000) : p.time, value: v }));
                    s.setData(sortAscUnique(points));
                }
            } else if (typeof entry.data === 'object') {
                Object.keys(entry.data).forEach((k) => {
                    const key = `${uid}:${k}`;
                    expected.add(key);
                    if ((entry.attachToId || entry.attachToMeta) && typeof targetPaneIndex !== 'number') {
                        if (isLoggingEnabled) console.log('[SpecOverlay] defer create (no pane yet)[key]', { key, attachToId: entry.attachToId, attachToMeta: entry.attachToMeta });
                        return;
                    }
                    let s = ensureSpecSeries(key, targetPaneIndex);
                    if (s && typeof targetPaneIndex === 'number') {
                        try {
                            const currentPane = s.getPane && s.getPane();
                            const currentIdx = currentPane && currentPane.paneIndex ? currentPane.paneIndex() : undefined;
                            if (typeof currentIdx === 'number' && currentIdx !== targetPaneIndex) {
                                if (isLoggingEnabled) console.log('[SpecOverlay] relocating series[key]', { key, from: currentIdx, to: targetPaneIndex });
                                chart.removeSeries(s);
                                specSeriesRef.current.delete(key);
                                s = ensureSpecSeries(key, targetPaneIndex);
                            }
                        } catch { /* ignore */ }
                    }
                    if (s) {
                        const filtered = (entry.data[k] || [])
                            .map(p => ({ p, v: valueForPoint(p, k) }))
                            .filter(({ p, v }) => typeof p?.time === 'number' && Number.isFinite(p.time) && Number.isFinite(v));
                        if (isLoggingEnabled && filtered.length === 0 && Array.isArray(entry.data[k]) && entry.data[k].length > 0) {
                            const sample = entry.data[k][entry.data[k].length - 1];
                            console.warn('[SpecOverlay] No numeric points found for multi-series', { key, seriesKey: k, sampleKeys: Object.keys(sample || {}) });
                        }
                        if (isLoggingEnabled) console.log('[SpecOverlay] setData[key]', { key, points: filtered.length, attachToId: entry.attachToId });
                        const points = filtered.map(({ p, v }) => ({ time: p.time > 1000000000000 ? Math.floor(p.time/1000) : p.time, value: v }));
                        s.setData(sortAscUnique(points));
                    }
                });
            }
        });
        // Clean up series that are no longer present
        specSeriesRef.current.forEach((series, key) => {
            if (!expected.has(key)) {
                try { chart.removeSeries(series); } catch { /* ignore */ }
                specSeriesRef.current.delete(key);
            }
        });
    }, [specStreams, activeIndicators, isBacktest, ensureSpecSeries, isLoggingEnabled]);

    // Auto-unsubscribe orphan overlays when parent indicator is removed
    const missingParentSinceRef = useRef(new Map()); // uid -> timestamp
    useEffect(() => {
        if (isBacktest) return;
        if (!specStreams || typeof specStreams !== 'object') return;
        const indicatorIds = new Set(activeIndicators.map(i => i.id));
        Object.entries(specStreams).forEach(([uid, entry]) => {
            if (!entry) return;
            const parentId = entry.attachToId || entry.attachToMeta?.parentId;
            const meta = entry.attachToMeta;
            const parentPresentByMeta = meta ? activeIndicators.some(i => {
                if (String(i.name).toLowerCase() !== String(meta.parentName).toLowerCase()) return false;
                const a = JSON.stringify(i.params||{});
                const b = JSON.stringify(meta.parentParams||{});
                return a === b;
            }) : false;
            if (!parentId && !parentPresentByMeta) return;
            if (!indicatorIds.has(parentId) && !parentPresentByMeta) {
                try {
                    // Grace period: parent can be temporarily missing during TF/symbol/account switches
                    const now = Date.now();
                    const first = missingParentSinceRef.current.get(uid) || now;
                    missingParentSinceRef.current.set(uid, first);
                    const GRACE_MS = 2000;
                    if (now - first < GRACE_MS) return; // wait and see if parent comes back

                    // remove any existing overlay series for this uid
                    const chart = chartRef.current;
                    if (chart) {
                        // remove both single and multi keys
                        const keys = [];
                        if (Array.isArray(entry.data)) keys.push(`${uid}:value`);
                        else if (entry.data && typeof entry.data === 'object') {
                            Object.keys(entry.data).forEach(k => keys.push(`${uid}:${k}`));
                        }
                        keys.forEach(k => {
                            const s = specSeriesRef.current.get(k);
                            if (s) {
                                const pane = s.getPane && s.getPane();
                                try { chart.removeSeries(s); } catch { /* ignore */ }
                                specSeriesRef.current.delete(k);
                                // if pane is empty and not main, remove it
                                try {
                                    if (pane && typeof pane.getSeries === 'function' && pane.paneIndex && pane.paneIndex() > 0) {
                                        if ((pane.getSeries()?.length || 0) === 0) {
                                            chart.removePane(pane.paneIndex());
                                        }
                                    }
                                } catch { /* ignore */ }
                            }
                        });
                    }
                    unsubscribeSpec && unsubscribeSpec(uid);
                    missingParentSinceRef.current.delete(uid);
                } catch { /* ignore */ }
            } else {
                // parent present; clear any grace timers
                if (missingParentSinceRef.current.has(uid)) missingParentSinceRef.current.delete(uid);
            }
        });
    }, [activeIndicators, specStreams, isBacktest, unsubscribeSpec]);

    // Timeframe overlay options (fallback if not provided)
    const defaultTimeframes = [
        { label: '1M', value: 'M1' },
        { label: '5M', value: 'M5' },
        { label: '15M', value: 'M15' },
        { label: '30M', value: 'M30' },
        { label: '1H', value: 'H1' },
        { label: '4H', value: 'H4' },
        { label: '1D', value: 'D1' },
        { label: '1W', value: 'W1' },
        { label: '1MN', value: 'MN1' },
    ];
    const tfOptions = timeframeOptions && Array.isArray(timeframeOptions) ? timeframeOptions : defaultTimeframes;
    const showChartControls = !isBacktest || showControls;
    const chartControls = showChartControls ? (
        <IndicatorControls
            onAddIndicator={handleAddIndicator}
            onRemoveIndicator={handleRemoveIndicator}
            onUpdateIndicatorParams={handleUpdateIndicatorParams}
            activeIndicators={activeIndicators}
            availableIndicators={availableIndicators}
            chartProfiles={chartProfiles}
            selectedProfile={selectedProfile}
            onLoadProfile={handleLoadProfile}
            onSaveProfile={handleSaveProfile}
            onDeleteProfile={handleDeleteProfile}
            onUpdateIndicatorVisuals={handleUpdateIndicatorVisuals}
            compact={controlsCompact}
            overlay={controlsOverlay}
            toolbarTitle={chartTitle}
            showRecentTrades={showRecentTrades}
            onToggleRecentTrades={onToggleRecentTrades}
            showVolume={showVolume}
            onToggleVolume={() => setShowVolume(v => !v)}
            isFullscreen={isChartFullscreen}
            onToggleFullscreen={fullscreenSupported ? handleToggleChartFullscreen : undefined}
            timeframeOptions={tfOptions}
            selectedTimeframe={selectedTimeframe}
            onTimeframeChange={onTimeframeChange}
            activeDrawingTool={activeDrawingTool}
            onSelectDrawingTool={handleSelectDrawingTool}
            onDeleteSelectedDrawing={handleDeleteSelectedDrawing}
            onDeleteAllDrawings={handleDeleteAllDrawings}
            onUpdateDrawing={handleUpdateDrawing}
            onSelectDrawing={handleSelectDrawing}
            drawingSelection={drawingSelection}
            selectedDrawingId={selectedDrawingId}
            drawingCount={drawingCount}
            drawings={drawings}
        />
    ) : null;

    return (
        <div
            ref={chartShellRef}
            className="trading-chart-shell w-full h-full min-h-[300px] flex flex-col bg-background"
        >
            {!controlsOverlay && chartControls}
            <div className={`${controlsOverlay ? 'p-0' : 'p-2 sm:p-3'} relative w-full flex-1 min-h-0`}>
                {controlsOverlay && chartControls && (
                    <div className="absolute left-2 right-[72px] top-2 z-40">
                        {chartControls}
                    </div>
                )}
                {isBacktest && !showControls && (
                    <div className="absolute top-2 left-2 z-30 flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-7 px-2 py-0 text-xs" onClick={() => setShowVolume(v => !v)} title="Toggle Volume Bars">
                            {showVolume ? 'Hide Vol' : 'Show Vol'}
                        </Button>
                    </div>
                )}
                {isBacktest && showVolume && backtestVolumeOverlayLabels.length > 0 && (
                    <div className="absolute z-30 flex flex-col gap-1 left-2" style={{ top: `${(paneTops[volumePaneIndexRef.current ?? 0] ?? 0) + 4}px` }}>
                        {backtestVolumeOverlayLabels.map(l => (
                            <div key={l.id} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-slate-900/70 backdrop-blur text-[10px] leading-tight text-slate-200 border border-slate-700 shadow-sm">
                                <span className="inline-block w-2 h-2 rounded" style={{ background: l.color }} />
                                <span className="select-none">{l.text}</span>
                            </div>
                        ))}
                    </div>
                )}
                {/* Removed demo EMA(RSI) buttons */}
                {showTimeframeOverlay && !isBacktest && !onTimeframeChange && (
                    <div className="absolute top-2 left-2 z-20 flex items-center gap-2">
                        <CustomSelect
                            options={tfOptions}
                            value={selectedTimeframe}
                            onChange={(val) => onTimeframeChange && onTimeframeChange(val)}
                            placeholder="TF"
                            className="w-36"
                            dropdownFitContent={true}
                            optionPaddingLeft={16}
                        />
                    </div>
                )}
                {/* Pane indicator chips (inside panes, top-left). Main price pane (0) excluded. */}
                {paneLabels.filter(l => l.paneIndex !== 0).map(l => {
                    const top = (paneTops[l.paneIndex] ?? (6 + (l.paneIndex * 120))) + 2;
                    return (
                        <div key={`${l.paneIndex}-${l.id}`}
                             className="absolute left-3 z-30"
                             style={{ top: `${top}px` }}>
                            <div className="flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 rounded bg-slate-800/70 backdrop-blur text-[11px] leading-tight text-slate-200/90 shadow-sm border border-slate-700/60">
                                <span className="mr-1 select-none">{l.text}</span>
                                <div className="flex items-center gap-0.5 ml-1">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-textSecondary hover:text-destructive" onClick={() => toggleOverlayVisibilityById(l.id)} title="Hide/Show">
                                        {isOverlayVisible(l.id) ? <IconEye/> : <IconEyeOff/>}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-secondary hover:text-textPrimary" onClick={() => openOverlayVisualEditor(l.id)} title="Edit visuals">
                                        <IconSliders/>
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-destructive hover:text-destructiveHover" onClick={() => handleRemoveIndicator(l.id)} title="Remove indicator">
                                        <IconTrash/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {/* Overlay indicator labels (main pane, top-left) with actions */}
                {!isBacktest && overlayLabels.length > 0 && (
                    <div className="absolute top-3 left-3 z-30 flex flex-col gap-1">
                        {overlayLabels.map((l) => (
                <div key={l.id} className="flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 rounded bg-slate-800/70 backdrop-blur text-[11px] leading-tight text-slate-200/90 shadow-sm border border-slate-700/60">
                                <span className="mr-1 select-none">{l.text}</span>
                                <div className="flex items-center gap-0.5 ml-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-textSecondary hover:text-destructive" onClick={() => toggleOverlayVisibilityById(l.id)} title="Hide/Show">
                                        {isOverlayVisible(l.id) ? <IconEye/> : <IconEyeOff/>}
                                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-secondary hover:text-textPrimary" onClick={() => openOverlayVisualEditor(l.id)} title="Edit visuals">
                                        <IconSliders/>
                                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-destructive hover:text-destructiveHover" onClick={() => handleRemoveIndicator(l.id)} title="Remove indicator">
                                        <IconTrash/>
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="relative w-full h-full">
                    <div
                        ref={chartContainerRef}
                        className="w-full h-full"
                    />
                    {/* Drawing tools canvas overlay – sits on top of the chart */}
                    <canvas
                        ref={drawingCanvasRef}
                        className="absolute inset-0 w-full h-full"
                        style={{ pointerEvents: 'none', zIndex: 25 }}
                    />
                </div>

            </div>
        </div>
     );
 };

 export default TradingChart;

// Icons (copied to match IndicatorControls)
const IconEye = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);
const IconEyeOff = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.94"/>
        <path d="M1 1l22 22"/>
    </svg>
);
const IconSliders = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3"/>
        <circle cx="4" cy="14" r="2"/>
        <circle cx="12" cy="12" r="2"/>
        <circle cx="20" cy="16" r="2"/>
    </svg>
);
const IconTrash = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

// Detect multi-output indicators represented as array-of-objects with multiple data_keys
// function isMultiOutputArrayIndicator(indicator) {
//     return Array.isArray(indicator?.data) && Array.isArray(indicator?.data_keys) && indicator.data_keys.length > 1;
// }

// Build a short label like "EMA 21" or "MACD 12/26/9" when disambiguation is needed
function buildOverlayLabel(name, params, includeParams) {
    const base = String(name || '').trim();
    if (!includeParams) return base;
    const p = params || {};
    const low = base.toLowerCase();
    // Common patterns
    if (low.includes('ema') || low === 'ema') {
        const len = p.length ?? p.period ?? p.n ?? p.window ?? p.len;
        return len != null ? `${base} ${len}` : base;
    }
    if (low.includes('sma') || low === 'sma') {
        const len = p.length ?? p.period ?? p.n ?? p.window ?? p.len;
        return len != null ? `${base} ${len}` : base;
    }
    if (low === 'rsi') {
        const len = p.length ?? p.period ?? p.window ?? p.len ?? p.n;
        return len != null ? `${base} ${len}` : base;
    }
    if (low === 'macd') {
        const fast = p.fast ?? p.fastLength ?? p.fast_period;
        const slow = p.slow ?? p.slowLength ?? p.slow_period;
        const sig = p.signal ?? p.signalLength ?? p.signal_period;
        const trio = [fast, slow, sig].filter(v => v != null).join('/');
        return trio ? `${base} ${trio}` : base;
    }
    if (low.includes('boll') || low === 'bb' || low === 'bollinger bands') {
        const len = p.length ?? p.period ?? p.n ?? p.window ?? p.len;
        const dev = p.std ?? p.deviation ?? p.deviations ?? p.stdDev ?? p.k;
        const duo = [len, dev].filter(v => v != null).join('/');
        return duo ? `${base} ${duo}` : base;
    }
    // Generic fallback: show first 1-3 numeric params
    const numericEntries = Object.entries(p).filter(([, v]) => typeof v === 'number');
    if (numericEntries.length === 0) return base;
    const short = numericEntries.slice(0, 3).map(([, v]) => v).join('/');
    return short ? `${base} ${short}` : base;
}

// Normalize an array of raw points to [{time, value}] selecting a numeric value by priority
function keyVariants(k) {
    if (!k || typeof k !== 'string') return [];
    const snake = k;
    const parts = k.split(/[_-]/g);
    const camel = parts[0] + parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
    const pascal = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
    const flat = parts.join('');
    return [snake, camel, pascal, flat];
}

function getFirstNumericByKeyVariants(obj, keyHint) {
    if (!obj || typeof obj !== 'object' || !keyHint) return undefined;
    for (const k of keyVariants(keyHint)) {
        const v = obj[k];
        if (Number.isFinite(v)) return v;
    }
    return undefined;
}

function normalizeSeriesPoints(arr, keyHint) {
    if (!Array.isArray(arr)) return [];
    return arr
        .filter(d => d && typeof d.time === 'number')
        .map(d => {
            let v = undefined;
            // Prefer key-specific values first
            v = getFirstNumericByKeyVariants(d, keyHint);
            if (v === undefined && d.data && typeof d.data === 'object') v = getFirstNumericByKeyVariants(d.data, keyHint);
            if (v === undefined && d.value && typeof d.value === 'object') v = getFirstNumericByKeyVariants(d.value, keyHint);
            if (v === undefined && d.outputs && typeof d.outputs === 'object') v = getFirstNumericByKeyVariants(d.outputs, keyHint);
            // Then generic forms
            if (v === undefined && Number.isFinite(d.value)) v = d.value;
            if (v === undefined && Number.isFinite(d.v)) v = d.v;
            if (v === undefined && d.data && typeof d.data === 'object') {
                for (const k of Object.keys(d.data)) {
                    const val = d.data[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            if (v === undefined && d.value && typeof d.value === 'object') {
                for (const k of Object.keys(d.value)) {
                    const val = d.value[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            if (v === undefined && d.outputs && typeof d.outputs === 'object') {
                for (const k of Object.keys(d.outputs)) {
                    const val = d.outputs[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            if (v === undefined) {
                for (const k of Object.keys(d)) {
                    if (k === 'time') continue;
                    const val = d[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            let t = d.time;
            if (t > 1000000000000) t = Math.floor(t / 1000);
            return v === undefined ? { time: t } : { time: t, value: v };
        })
        .sort((a, b) => a.time - b.time);
}

function detectOutputKeysFromData(indicator) {
    if (!Array.isArray(indicator?.data) || indicator.data.length === 0) return [];
    const sample = indicator.data.find(x => x && typeof x === 'object');
    if (!sample) return [];
    if (sample.outputs && typeof sample.outputs === 'object') return Object.keys(sample.outputs);
    if (sample.value && typeof sample.value === 'object') return Object.keys(sample.value);
    if (sample.data && typeof sample.data === 'object') return Object.keys(sample.data);
    // If flat keys present (excluding time/value)
    const flat = Object.keys(sample).filter(k => k !== 'time' && k !== 'value' && k !== 'outputs' && k !== 'data');
    // Only return if there are multiple numeric-looking keys
    if (flat.length > 1) return flat;
    return [];
}

// Create masked versions of points keeping only values with desired sign; others become whitespace bars
// Note: previously used dual-series masking for slope outputs was removed per design change.

function getOutputKeys(indicator, indicatorMeta) {
    if (Array.isArray(indicator?.data_keys) && indicator.data_keys.length > 0) return indicator.data_keys;
    const fromData = detectOutputKeysFromData(indicator);
    if (fromData.length > 0) return fromData;
    if (Array.isArray(indicatorMeta?.outputs) && indicatorMeta.outputs.length > 0) return indicatorMeta.outputs;
    return [];
}

// ---- Overlay label actions helpers ----
// function getSeriesKeysForIndicator(indicator, availableIndicators) {
//     const meta = getIndicatorMeta(availableIndicators, indicator?.name);
//     const schemaKeys = meta?.visual_schema?.series ? Object.keys(meta.visual_schema.series) : [];
//     const defaultKeys = meta?.visual_defaults?.series ? Object.keys(meta.visual_defaults.series) : [];
//     const keys = schemaKeys.length ? schemaKeys : (defaultKeys.length ? defaultKeys : ['default']);
//     return keys;
// }

function getEffectiveVisuals(indicator, availableIndicators) {
    const meta = getIndicatorMeta(availableIndicators, indicator?.name);
    const defaults = meta?.visual_defaults || {};
    const overrides = indicator?.visual_overrides || {};
    return deepMerge(defaults, overrides);
}

// Instance-level helpers bound in component scope
// These rely on activeIndicators, availableIndicators, and handleUpdateIndicatorVisuals

// ---- Pane chip portal management (DOM attach inside panes) ----
function findPaneContainers(chartContainerEl) {
    if (!chartContainerEl) return [];
    // Heuristic: direct children that include canvases represent panes
    const all = Array.from(chartContainerEl.querySelectorAll(':scope > div'));
    const panes = all.filter(el => el.querySelector && el.querySelector('canvas'));
    if (panes.length > 0) return panes;
    // Fallback: any descendant with a canvas, grouped by top-level ancestor
    const canvases = Array.from(chartContainerEl.querySelectorAll('canvas'));
    const topGroups = new Map();
    canvases.forEach((cv) => {
        let p = cv.parentElement;
        let top = null;
        while (p && p !== chartContainerEl) {
            top = p;
            p = p.parentElement;
        }
        if (top) topGroups.set(top, true);
    });
    return Array.from(topGroups.keys());
}

function renderPaneChip(root, content) {
    try { root.render(content); } catch { /* ignore */ }
}

function PaneChip({ label, isVisible, onToggle, onEdit, onRemove }) {
    return (
        <div className="absolute left-3 top-2 z-30">
            <div className="flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 rounded bg-slate-800/70 backdrop-blur text-[11px] leading-tight text-slate-200/90 shadow-sm border border-slate-700/60">
                <span className="mr-1 select-none">{label}</span>
                <div className="flex items-center gap-0.5 ml-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-textSecondary hover:text-destructive" onClick={onToggle} title="Hide/Show">
                        {isVisible ? <IconEye/> : <IconEyeOff/>}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-secondary hover:text-textPrimary" onClick={onEdit} title="Edit visuals">
                        <IconSliders/>
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-destructive hover:text-destructiveHover" onClick={onRemove} title="Remove indicator">
                        <IconTrash/>
                    </Button>
                </div>
            </div>
        </div>
    );
}

function attachPaneChips(paneLabels, chartEl, isVisibleCb, toggleCb, editCb, removeCb) {
    if (!chartEl) return;
    const panes = findPaneContainers(chartEl);
    if (!panes || panes.length === 0) return;

    // Build map index->paneElement based on DOM order (0=main)
    const paneIndexToEl = new Map();
    panes.forEach((el, idx) => paneIndexToEl.set(idx, el));

    const rootsRef = window.__paneChipRoots || (window.__paneChipRoots = new Map());

    // For each label (paneIndex>=1), create/attach a root and render content
    paneLabels.filter(l => l && typeof l.paneIndex === 'number' && l.paneIndex > 0).forEach((l) => {
        const paneEl = paneIndexToEl.get(l.paneIndex);
        if (!paneEl) return;
        // Create or reuse mount element
        const key = `pane-chip-${l.paneIndex}`;
        let mount = paneEl.querySelector(`.pane-chip-anchor[data-k='${key}']`);
        if (!mount) {
            mount = document.createElement('div');
            mount.className = 'pane-chip-anchor';
            mount.dataset.k = key;
            mount.style.position = 'relative';
            paneEl.appendChild(mount);
        }
        let root = rootsRef.get(key);
        if (!root) {
            root = createRoot(mount);
            rootsRef.set(key, root);
        }
        renderPaneChip(root, (
            <PaneChip
                label={l.text}
                isVisible={isVisibleCb?.(l.id) ?? true}
                onToggle={() => toggleCb?.(l.id)}
                onEdit={() => editCb?.(l.id)}
                onRemove={() => removeCb?.(l.id)}
            />
        ));
    });
}
